/* ═══════════════════════════════════════════════════════════════════
   DTF PRINT DEPOT — THEME JS
   Global enhancement engines only. Header, footer, and essential page
   content are rendered by Liquid sections and blocks.
   ═══════════════════════════════════════════════════════════════════ */
(function(){
'use strict';

/* ---------- three signature View Transitions ---------- */
const reducedMotionPreference = window.matchMedia?.('(prefers-reduced-motion: reduce)');

function userPrefersReducedMotion(){
  return reducedMotionPreference?.matches === true;
}

function canUseViewTransitions(){
  return typeof document.startViewTransition === 'function' && !userPrefersReducedMotion();
}

function cancelViewTransitionOnInteraction(viewTransition){
  if (!viewTransition?.skipTransition || !viewTransition.finished) return;
  let listening = true;
  const cleanup = () => {
    if (!listening) return;
    listening = false;
    window.removeEventListener('pointerdown', cancel);
    window.removeEventListener('keydown', cancel);
  };
  const cancel = () => {
    viewTransition.skipTransition();
    cleanup();
  };

  window.addEventListener('pointerdown', cancel, { once: true });
  window.addEventListener('keydown', cancel, { once: true });
  viewTransition.finished.then(cleanup, cleanup);
}

async function runSignatureViewTransition(update, signature = ''){
  if (!canUseViewTransitions()) return update();

  let result;
  let viewTransition;
  const transitionRoot = document.documentElement;
  const clearSignature = () => {
    if (signature && transitionRoot.dataset.signatureViewTransition === signature) {
      delete transitionRoot.dataset.signatureViewTransition;
    }
  };
  if (signature) transitionRoot.dataset.signatureViewTransition = signature;
  try {
    viewTransition = document.startViewTransition(() => {
      result = update();
    });
  } catch (error) {
    clearSignature();
    return update();
  }

  cancelViewTransitionOnInteraction(viewTransition);
  viewTransition.finished.then(clearSignature, clearSignature);
  viewTransition.ready.catch(() => {});
  await viewTransition.updateCallbackDone;
  return result;
}

function activatePrimaryProductTransitionTarget(){
  const targets = [...document.querySelectorAll('[data-product-transition-target]')];
  document.querySelectorAll('[data-product-transition-target-active]').forEach(target => {
    target.removeAttribute('data-product-transition-target-active');
  });
  targets[0]?.setAttribute('data-product-transition-target-active', '');
}

document.addEventListener('click', event => {
  const link = event.target.closest?.('[data-product-transition-source]');
  const crossDocumentTransitions = window.DTFViewTransitions;
  if (
    !link
    || event.defaultPrevented
    || event.button !== 0
    || event.metaKey
    || event.ctrlKey
    || event.shiftKey
    || event.altKey
    || link.hasAttribute('download')
    || (link.target && link.target !== '_self')
    || !crossDocumentTransitions?.canUseCrossDocument?.()
  ) return;

  const targetUrl = crossDocumentTransitions.normalizeUrl?.(link.href) || '';
  const centerImage = link.querySelector('.trio > .t-c');
  if (!targetUrl || !centerImage || new URL(link.href, window.location.href).origin !== window.location.origin) return;

  document.querySelectorAll('[data-product-transition-source-active]').forEach(source => {
    source.removeAttribute('data-product-transition-source-active');
  });
  document.querySelectorAll('[data-product-transition-target-active]').forEach(target => {
    target.removeAttribute('data-product-transition-target-active');
  });
  link.setAttribute('data-product-transition-source-active', '');
  crossDocumentTransitions.markProductNavigation(targetUrl);
}, { capture: true });

window.addEventListener('pageshow', () => {
  document.querySelectorAll('[data-product-transition-source-active]').forEach(source => {
    source.removeAttribute('data-product-transition-source-active');
  });
  activatePrimaryProductTransitionTarget();
});
document.addEventListener('shopify:section:load', activatePrimaryProductTransitionTarget);
document.addEventListener('shopify:section:unload', activatePrimaryProductTransitionTarget);
document.addEventListener('shopify:section:reorder', activatePrimaryProductTransitionTarget);
activatePrimaryProductTransitionTarget();

/* Header and collection/search controls use a scroll-edge material instead of
   a permanent bar. Sticky geometry is read from layout so merchant fonts,
   browser text scaling, and Theme Editor section reloads stay aligned. */
const scrollEdgeRoot = document.documentElement;
let scrollEdgeHeader = null;
let scrollEdgeHeaderHost = null;
let scrollEdgeToolbars = [];
let scrollEdgeResizeObserver = null;
let scrollEdgeFrame = 0;
let scrollEdgeRefreshFrame = 0;

function isAtScrollEdge(element){
  if (!element?.isConnected || window.scrollY <= 0) return false;
  const style = getComputedStyle(element);
  if (style.position !== 'sticky' && style.position !== 'fixed') return false;
  const inset = Number.parseFloat(style.insetBlockStart || style.top);
  const edge = Number.isFinite(inset) ? inset : 0;
  const bounds = element.getBoundingClientRect();
  return bounds.height > 0 && bounds.bottom > edge + 1 && Math.abs(bounds.top - edge) <= 1.5;
}

function syncStickyHeaderSize(){
  const size = scrollEdgeHeader?.isConnected
    ? Math.max(0, Math.ceil(scrollEdgeHeader.getBoundingClientRect().height))
    : 0;
  scrollEdgeRoot.style.setProperty('--sticky-header-block-size', `${size}px`);
}

function updateScrollEdges(){
  scrollEdgeFrame = 0;
  scrollEdgeToolbars = scrollEdgeToolbars.filter(toolbar => toolbar.isConnected);

  const edges = [scrollEdgeHeader, ...scrollEdgeToolbars].filter(Boolean);
  edges.forEach(edge => {
    edge.dataset.scrollEdgeState = 'rest';
    edge.removeAttribute('data-scroll-edge-owner');
  });

  const headerIsStuck = isAtScrollEdge(scrollEdgeHeaderHost);
  if (scrollEdgeHeader) scrollEdgeHeader.dataset.scrollEdgeState = headerIsStuck ? 'stuck' : 'rest';

  const stuckToolbars = scrollEdgeToolbars.filter(toolbar => {
    const stuck = isAtScrollEdge(toolbar);
    toolbar.dataset.scrollEdgeState = stuck ? 'stuck' : 'rest';
    return stuck;
  });

  const owner = stuckToolbars[stuckToolbars.length - 1] || (headerIsStuck ? scrollEdgeHeader : null);
  owner?.setAttribute('data-scroll-edge-owner', 'true');
}

function queueScrollEdgeUpdate(){
  if (!scrollEdgeFrame) scrollEdgeFrame = requestAnimationFrame(updateScrollEdges);
}

function refreshScrollEdges(){
  scrollEdgeRefreshFrame = 0;
  scrollEdgeHeader = document.querySelector('[data-scroll-edge="header"]');
  scrollEdgeHeaderHost = scrollEdgeHeader;
  scrollEdgeToolbars = [...document.querySelectorAll('[data-scroll-edge="toolbar"]')];

  scrollEdgeResizeObserver?.disconnect();
  scrollEdgeResizeObserver = null;

  if (!scrollEdgeHeader && !scrollEdgeToolbars.length) {
    scrollEdgeRoot.classList.remove('scroll-edge-ready');
    scrollEdgeRoot.style.removeProperty('--sticky-header-block-size');
    return;
  }

  scrollEdgeRoot.classList.add('scroll-edge-ready');
  syncStickyHeaderSize();
  if ('ResizeObserver' in window && scrollEdgeHeader) {
    scrollEdgeResizeObserver = new ResizeObserver(() => {
      syncStickyHeaderSize();
      queueScrollEdgeUpdate();
    });
    scrollEdgeResizeObserver.observe(scrollEdgeHeader);
  }
  updateScrollEdges();
}

function queueScrollEdgeRefresh(){
  if (!scrollEdgeRefreshFrame) scrollEdgeRefreshFrame = requestAnimationFrame(refreshScrollEdges);
}

window.addEventListener('scroll', queueScrollEdgeUpdate, { passive:true });
window.addEventListener('resize', () => {
  syncStickyHeaderSize();
  queueScrollEdgeUpdate();
}, { passive:true });
window.addEventListener('pageshow', queueScrollEdgeRefresh);
document.addEventListener('shopify:section:load', queueScrollEdgeRefresh);
document.addEventListener('shopify:section:unload', queueScrollEdgeRefresh);
document.addEventListener('shopify:section:reorder', queueScrollEdgeRefresh);
document.fonts?.ready.then(queueScrollEdgeRefresh);
refreshScrollEdges();

/* toast */
let toastTimer;
let toastState = null;

function closeDepotToast(reason = 'dismiss'){
  const t = document.getElementById('toast');
  const previousState = toastState;
  if ((reason === 'action' || reason === 'dismiss' || reason === 'timeout' || reason === 'replace') && previousState?.deferCloseUntilResolved) {
    if (previousState.actionPending) return;
    clearTimeout(toastTimer);
    toastTimer = null;
    previousState.actionPending = true;
    t?.querySelector('[data-toast-action]')?.setAttribute('aria-disabled', 'true');
    t?.querySelector('[data-toast-close]')?.setAttribute('aria-disabled', 'true');
    if (reason === 'action') previousState.onAction?.();
    else previousState.onDismiss?.();
    return;
  }

  clearTimeout(toastTimer);
  toastTimer = null;
  if (t) {
    t.classList.remove('show', 'has-action');
    const action = t.querySelector('[data-toast-action]');
    const close = t.querySelector('[data-toast-close]');
    if (action) {
      action.hidden = true;
      action.removeAttribute('aria-disabled');
    }
    if (close) {
      close.hidden = true;
      close.removeAttribute('aria-disabled');
    }
  }

  toastState = null;
  if (!previousState) return;

  if (reason === 'action') previousState.onAction?.();
  else if (reason === 'dismiss' || reason === 'timeout' || reason === 'replace') previousState.onDismiss?.();
}

window.depotToast = function(msg, options = {}){
  const t = document.getElementById('toast');
  if (!t) return false;
  if (toastState) {
    closeDepotToast('replace');
    if (toastState) return false;
  }

  const message = t.querySelector('[data-toast-message]');
  const action = t.querySelector('[data-toast-action]');
  const close = t.querySelector('[data-toast-close]');
  if (message && msg) message.textContent = msg;
  const hasAction = Boolean(action && options.actionLabel && typeof options.onAction === 'function');
  if (action) {
    action.hidden = !hasAction;
    if (hasAction) action.textContent = options.actionLabel;
  }
  const hasDismiss = Boolean(close && options.dismissible);
  if (close) close.hidden = !hasDismiss;
  t.classList.toggle('has-action', hasAction);
  t.classList.add('show');
  const nextState = {
    onAction: hasAction ? options.onAction : null,
    onDismiss: typeof options.onDismiss === 'function' ? options.onDismiss : null,
    deferCloseUntilResolved: Boolean(hasAction && options.deferCloseUntilResolved),
    actionPending: false
  };
  toastState = nextState;
  const duration = Number.isFinite(options.duration) && options.duration > 0 ? options.duration : 2200;
  if (!options.persistent) toastTimer = setTimeout(() => closeDepotToast('timeout'), duration);
  return {
    close(){
      if (toastState === nextState) closeDepotToast('resolved');
    }
  };
};
document.addEventListener('click', e => {
  if (e.target.closest?.('[data-toast-close]')) {
    e.preventDefault();
    closeDepotToast('dismiss');
    return;
  }
  if (e.target.closest?.('[data-toast-action]')) {
    e.preventDefault();
    closeDepotToast('action');
    return;
  }
  const el = e.target.closest('[data-toast]');
  if (el) { e.preventDefault(); window.depotToast(el.dataset.toast); }
});

/* Collection sorting remains server-rendered; this only submits Shopify's
   native sort option while the form preserves active storefront filters. */
document.addEventListener('change', e => {
  const select = e.target.closest?.('[data-collection-sort]');
  if (!select || !select.form) return;
  if (typeof select.form.requestSubmit === 'function') select.form.requestSubmit();
  else select.form.submit();
});

/* ---------- RADIAL WHEEL ENGINE (sections/half-moon-wheel + hero-fan) ----------
   Cards sit tangent on a circle; step = 2·asin((W/2+gap)/(R+H/2)) so
   neighbouring corners kiss. Auto-inits every [data-wheel] viewport.
   data-wheel='{"desktop":{...},"mobile":{...},"buttons":true,"ground":true,"highlight":true}' */
function radialWheel(viewport){
  if (viewport.radialWheelCleanup) return;
  const opts = JSON.parse(viewport.dataset.wheel);
  const hub = viewport.querySelector('[data-wheel-hub]');
  if (!hub) return;
  const ground = viewport.querySelector('.wheel-ground');
  const previous = viewport.querySelector('[data-wheel-prev]');
  const next = viewport.querySelector('[data-wheel-next]');
  const status = viewport.querySelector('[data-wheel-status]');
  const statusTemplate = status?.dataset.wheelStatusTemplate || '';
  const positionTemplate = status?.dataset.wheelPositionTemplate || '';
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const defaultTabIndex = viewport.getAttribute('tabindex');
  const defaultKeyshortcuts = viewport.getAttribute('aria-keyshortcuts');
  let cards = [];
  let n = 0;
  let cfg, rot = 0, dragging = false, activePointerId = null, lastX = 0, moved = 0;
  let dragStartRot = 0, dragDelta = 0, currentIndex = -1, layoutFrame = 0;
  let suppressNextPointerClick = false;
  function layout(){
    if (layoutFrame) {
      cancelAnimationFrame(layoutFrame);
      layoutFrame = 0;
    }
    cards = [...hub.children];
    n = cards.length;
    if (n > 1) {
      if (defaultTabIndex === null) viewport.removeAttribute('tabindex');
      else viewport.setAttribute('tabindex', defaultTabIndex);
      if (defaultKeyshortcuts === null) viewport.removeAttribute('aria-keyshortcuts');
      else viewport.setAttribute('aria-keyshortcuts', defaultKeyshortcuts);
    } else {
      viewport.setAttribute('tabindex', '-1');
      viewport.removeAttribute('aria-keyshortcuts');
    }
    const profile = matchMedia('(max-width: 44.9375em)').matches ? opts.mobile : opts.desktop;
    const rootSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const rootScale = rootSize / 16;
    cfg = Object.fromEntries(
      Object.entries(profile).map(([key, value]) => [key, typeof value === 'number' ? value * rootScale : value])
    );
    cfg.step = 2 * Math.asin((cfg.w / 2 + cfg.gap) / (cfg.r + cfg.h / 2));
    cfg.maxRot = Math.max(0, (n - 1) / 2 * cfg.step);
    rot = Math.max(-cfg.maxRot, Math.min(cfg.maxRot, rot));
    viewport.style.height = cfg.vh + 'px';
    hub.style.top = (cfg.r + cfg.h / 2 + cfg.pad) + 'px';
    hub.style.transform = 'none';
    hub.classList.add('is-wheel-ready');
    cards.forEach(c => {
      c.style.width = cfg.w + 'px';
      c.style.height = cfg.h + 'px';
      c.style.margin = (-cfg.h / 2) + 'px 0 0 ' + (-cfg.w / 2) + 'px';
    });
    if (ground) {
      const gr = cfg.r - cfg.h / 2 - 22 * rootScale;
      ground.style.width = ground.style.height = gr * 2 + 'px';
      ground.style.top = (cfg.r + cfg.h / 2 + cfg.pad - gr) + 'px';
    }
    viewport.classList.add('dragging');
    render();
    if (motionPreference.matches) {
      viewport.classList.remove('dragging');
    } else {
      layoutFrame = requestAnimationFrame(() => {
        layoutFrame = 0;
        if (!dragging) viewport.classList.remove('dragging');
      });
    }
  }
  function render(){
    let fi = -1, fd = 1e9;
    cards.forEach((c, i) => {
      const a = (i - (n - 1) / 2) * cfg.step + rot;
      const deg = a * 180 / Math.PI;
      if (Math.abs(deg) > 96) {
        if (c.contains(document.activeElement)) viewport.focus({ preventScroll: true });
        c.style.visibility = 'hidden';
        return;
      }
      c.style.visibility = 'visible';
      c.style.transform = 'rotate(' + deg + 'deg) translateY(' + (-cfg.r) + 'px)';
      if (Math.abs(a) < fd) { fd = Math.abs(a); fi = i; }
    });
    currentIndex = fd <= cfg.step / 2 ? fi : -1;
    cards.forEach((card, index) => {
      const isCurrent = index === currentIndex;
      if (isCurrent) card.setAttribute('aria-current', 'true');
      else card.removeAttribute('aria-current');
      if (opts.highlight) card.classList.toggle('is-front', isCurrent);
    });
    const atPreviousEnd = n < 2 || rot >= cfg.maxRot - Number.EPSILON;
    const atNextEnd = n < 2 || rot <= -cfg.maxRot + Number.EPSILON;
    if (previous) previous.disabled = atPreviousEnd;
    if (next) next.disabled = atNextEnd;
  }
  function announceCurrent(){
    if (!status || currentIndex < 0) return;
    const title = cards[currentIndex]?.querySelector('.fan-card__title,.wcard__title')?.textContent.trim() || '';
    const template = title ? statusTemplate : positionTemplate;
    if (!template) return;
    const message = template
      .replace('__TITLE__', title)
      .replace('__INDEX__', String(currentIndex + 1))
      .replace('__TOTAL__', String(n));
    if (status.textContent !== message) status.textContent = message;
  }
  const clamp = v => Math.max(-cfg.maxRot, Math.min(cfg.maxRot, v));
  const spin = dir => {
    const targetRot = clamp(rot + dir * cfg.step * 2);
    if (Math.abs(targetRot - rot) < 1e-9) return;
    rot = targetRot;
    render();
    announceCurrent();
  };
  const pointerdown = e => {
    if (!e.isPrimary || (e.pointerType === 'mouse' && e.button !== 0)) return;
    suppressNextPointerClick = false;
    dragging = true;
    activePointerId = e.pointerId;
    moved = 0;
    dragDelta = 0;
    dragStartRot = rot;
    lastX = e.clientX;
    viewport.classList.add('dragging');
  };
  const pointermove = e => {
    if (!dragging || e.pointerId !== activePointerId) return;
    const dx = e.clientX - lastX;
    lastX = e.clientX;
    moved += Math.abs(dx);
    dragDelta += dx;
    if (!motionPreference.matches) {
      rot = clamp(rot + dx / cfg.r);
      render();
    }
    if (moved > 8 && !viewport.hasPointerCapture(e.pointerId)) {
      // Card hover yields only to a real drag, so a plain click never drops the hover lift.
      viewport.classList.add('is-drag-moved');
      try { viewport.setPointerCapture(e.pointerId); } catch (error) { /* synthetic or ended pointer */ }
    }
  };
  const stopDragging = restoreStart => {
    const pointerId = activePointerId;
    if (restoreStart) {
      rot = dragStartRot;
      render();
    }
    dragging = false;
    activePointerId = null;
    viewport.classList.remove('dragging', 'is-drag-moved');
    if (pointerId !== null && viewport.hasPointerCapture(pointerId)) {
      try { viewport.releasePointerCapture(pointerId); } catch (error) { /* ended pointer */ }
    }
  };
  const end = e => {
    if (!dragging || e.pointerId !== activePointerId) return;
    const wasDrag = moved > 8;
    if (
      moved > 8
      && document.activeElement instanceof Element
      && document.activeElement !== viewport
      && viewport.contains(document.activeElement)
    ) {
      viewport.focus({ preventScroll: true });
    }
    const reducedStep = motionPreference.matches && e.type === 'pointerup' && Math.abs(dragDelta) > 8;
    const reducedDirection = dragDelta > 0 ? 1 : -1;
    const shouldRestore = e.type !== 'pointerup' || motionPreference.matches;
    stopDragging(shouldRestore);
    if (reducedStep) spin(reducedDirection);
    else if (wasDrag) announceCurrent();
  };
  const pointerleave = e => {
    if (dragging && e.pointerId === activePointerId && !viewport.hasPointerCapture(e.pointerId)) end(e);
  };
  const click = e => {
    const suppressPointerClick = e.detail > 0 && (suppressNextPointerClick || moved > 8);
    suppressNextPointerClick = false;
    moved = 0;
    if (suppressPointerClick) {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  const keydown = e => {
    if (
      e.defaultPrevented
      || e.isComposing
      || e.altKey
      || e.ctrlKey
      || e.metaKey
      || e.shiftKey
    ) return;
    if (e.key === 'Escape' && dragging) {
      suppressNextPointerClick = true;
      stopDragging(true);
      announceCurrent();
      e.preventDefault();
      return;
    }
    if (e.target !== viewport) return;
    if (e.key === 'ArrowLeft')  { spin(1);  e.preventDefault(); }
    if (e.key === 'ArrowRight') { spin(-1); e.preventDefault(); }
  };
  const dragstart = e => e.preventDefault();
  const previousClick = () => spin(1);
  const nextClick = () => spin(-1);
  const motionPreferenceChange = () => {
    viewport.classList.toggle('motion-reduced', motionPreference.matches);
    if (motionPreference.matches && dragging) {
      suppressNextPointerClick = true;
      stopDragging(true);
      announceCurrent();
    }
  };

  viewport.addEventListener('pointerdown', pointerdown);
  viewport.addEventListener('pointermove', pointermove);
  viewport.addEventListener('pointerup', end);
  viewport.addEventListener('pointercancel', end);
  viewport.addEventListener('pointerleave', pointerleave);
  viewport.addEventListener('click', click, true);
  viewport.addEventListener('keydown', keydown);
  viewport.addEventListener('dragstart', dragstart);
  previous?.addEventListener('click', previousClick);
  next?.addEventListener('click', nextClick);
  if (motionPreference.addEventListener) motionPreference.addEventListener('change', motionPreferenceChange);
  else motionPreference.addListener(motionPreferenceChange);
  motionPreferenceChange();
  window.addEventListener('resize', layout);
  const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(layout) : null;
  resizeObserver?.observe(document.documentElement);

  viewport.radialWheelLayout = layout;
  viewport.radialWheelCleanup = () => {
    viewport.removeEventListener('pointerdown', pointerdown);
    viewport.removeEventListener('pointermove', pointermove);
    viewport.removeEventListener('pointerup', end);
    viewport.removeEventListener('pointercancel', end);
    viewport.removeEventListener('pointerleave', pointerleave);
    viewport.removeEventListener('click', click, true);
    viewport.removeEventListener('keydown', keydown);
    viewport.removeEventListener('dragstart', dragstart);
    previous?.removeEventListener('click', previousClick);
    next?.removeEventListener('click', nextClick);
    if (motionPreference.removeEventListener) motionPreference.removeEventListener('change', motionPreferenceChange);
    else motionPreference.removeListener(motionPreferenceChange);
    window.removeEventListener('resize', layout);
    resizeObserver?.disconnect();
    if (layoutFrame) {
      cancelAnimationFrame(layoutFrame);
      layoutFrame = 0;
    }
    if (activePointerId !== null && viewport.hasPointerCapture(activePointerId)) {
      try { viewport.releasePointerCapture(activePointerId); } catch (error) { /* ended pointer */ }
    }
    dragging = false;
    activePointerId = null;
    viewport.classList.remove('dragging', 'is-drag-moved');
    viewport.classList.remove('motion-reduced');
    cards.forEach(card => card.removeAttribute('aria-current'));
    if (status) status.textContent = '';
    if (defaultTabIndex === null) viewport.removeAttribute('tabindex');
    else viewport.setAttribute('tabindex', defaultTabIndex);
    if (defaultKeyshortcuts === null) viewport.removeAttribute('aria-keyshortcuts');
    else viewport.setAttribute('aria-keyshortcuts', defaultKeyshortcuts);
    hub.classList.remove('is-wheel-ready');
    delete viewport.radialWheelLayout;
    delete viewport.radialWheelCleanup;
  };
  layout();
}
function initializeWheels(scope){
  if (scope.matches?.('[data-wheel]')) radialWheel(scope);
  scope.querySelectorAll?.('[data-wheel]').forEach(radialWheel);
}
function cleanupWheels(scope){
  if (scope.matches?.('[data-wheel]')) scope.radialWheelCleanup?.();
  scope.querySelectorAll?.('[data-wheel]').forEach(viewport => viewport.radialWheelCleanup?.());
}
initializeWheels(document);
document.addEventListener('shopify:section:load', e => initializeWheels(e.target));
document.addEventListener('shopify:section:unload', e => cleanupWheels(e.target));
document.addEventListener('shopify:section:reorder', e => initializeWheels(e.target));
document.addEventListener('shopify:block:select', e => e.target.closest('[data-wheel]')?.radialWheelLayout?.());

/* Product recommendation wheels are server-rendered through Shopify's
   recommendation section endpoint, then handed back to the radial engine. */
function initializeRecommendationWheel(section){
  if (section.recommendationWheelCleanup || !section.dataset.recommendationsUrl) return;
  const controller = new AbortController();
  let observer = null;
  let loading = false;

  const hideOnFailure = () => {
    section.removeAttribute('aria-busy');
    if (section.dataset.designMode !== 'true') section.hidden = true;
  };

  const load = () => {
    if (loading) return;
    loading = true;
    observer?.disconnect();
    fetch(section.dataset.recommendationsUrl, { signal: controller.signal })
      .then(response => {
        if (!response.ok) throw new Error('Recommendation request failed');
        return response.text();
      })
      .then(text => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const replacement = [...html.querySelectorAll('[data-half-moon-wheel]')]
          .find(candidate => candidate.dataset.sectionId === section.dataset.sectionId);
        if (!replacement || replacement.dataset.recommendationsState !== 'performed') {
          hideOnFailure();
          return;
        }
        cleanupWheels(section);
        section.recommendationWheelCleanup?.();
        section.replaceWith(replacement);
        initializeWheels(replacement);
        initializeRecommendationWheels(replacement);
      })
      .catch(error => {
        if (error.name !== 'AbortError') hideOnFailure();
      });
  };

  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) load();
    }, { rootMargin: '0px 0px 400px 0px' });
    observer.observe(section);
  } else {
    load();
  }

  section.recommendationWheelCleanup = () => {
    observer?.disconnect();
    controller.abort();
    delete section.recommendationWheelCleanup;
  };
}
function initializeRecommendationWheels(scope){
  if (scope.matches?.('[data-recommendations-url]')) initializeRecommendationWheel(scope);
  scope.querySelectorAll?.('[data-recommendations-url]').forEach(initializeRecommendationWheel);
}
function cleanupRecommendationWheels(scope){
  if (scope.matches?.('[data-half-moon-wheel]')) scope.recommendationWheelCleanup?.();
  scope.querySelectorAll?.('[data-half-moon-wheel]').forEach(section => section.recommendationWheelCleanup?.());
}
initializeRecommendationWheels(document);
document.addEventListener('shopify:section:load', e => initializeRecommendationWheels(e.target));
document.addEventListener('shopify:section:unload', e => cleanupRecommendationWheels(e.target));

/* ---------- process domino (sections/process-domino) ---------- */
function initializeDomino(row){
  if (row.dominoCleanup) return;
  const section = row.closest('[data-domino-section]');
  const skipButton = section?.querySelector('[data-domino-skip]');
  const cards = [...row.querySelectorAll('.dcard')];
  const finalCard = cards[cards.length - 1];
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  let io = null;
  let revealed = false;
  let temporaryFocusTarget = null;
  const hideSkip = () => {
    if (!skipButton) return;
    const shouldMoveFocus = document.activeElement === skipButton;
    skipButton.hidden = true;
    if (shouldMoveFocus) {
      const focusTarget = cards[0] || row;
      if (!focusTarget.hasAttribute('tabindex')) {
        focusTarget.setAttribute('tabindex', '-1');
        temporaryFocusTarget = focusTarget;
      }
      focusTarget.focus({ preventScroll: true });
    }
  };
  const reveal = () => {
    if (revealed) return;
    revealed = true;
    row.classList.add('in');
    io?.disconnect();
    if (!finalCard) hideSkip();
  };
  const skip = () => {
    revealed = true;
    row.classList.add('is-motion-skipped', 'in');
    io?.disconnect();
    hideSkip();
  };
  const transitionEnd = event => {
    if (event.target === finalCard && event.propertyName === 'transform') hideSkip();
  };
  const motionPreferenceChange = () => {
    if (motionPreference.matches) skip();
  };
  skipButton?.addEventListener('click', skip);
  row.addEventListener('transitionend', transitionEnd);
  if (motionPreference.addEventListener) motionPreference.addEventListener('change', motionPreferenceChange);
  else motionPreference.addListener(motionPreferenceChange);

  if (motionPreference.matches || !('IntersectionObserver' in window)) {
    skip();
  } else {
    if (skipButton) skipButton.hidden = false;
    io = new IntersectionObserver(entries => {
      entries.forEach(entry => { if (entry.isIntersecting) reveal(); });
    }, { threshold: 0.35 });
    io.observe(row);
  }

  row.dominoReveal = () => {
    if (motionPreference.matches) skip();
    else reveal();
  };
  row.dominoSkip = skip;
  row.dominoCleanup = () => {
    io?.disconnect();
    skipButton?.removeEventListener('click', skip);
    row.removeEventListener('transitionend', transitionEnd);
    if (motionPreference.removeEventListener) motionPreference.removeEventListener('change', motionPreferenceChange);
    else motionPreference.removeListener(motionPreferenceChange);
    row.classList.remove('in', 'is-motion-skipped');
    if (skipButton) skipButton.hidden = true;
    temporaryFocusTarget?.removeAttribute('tabindex');
    delete row.dominoReveal;
    delete row.dominoSkip;
    delete row.dominoCleanup;
  };
}
function initializeDominos(scope){
  if (scope.matches?.('[data-domino]')) initializeDomino(scope);
  scope.querySelectorAll?.('[data-domino]').forEach(initializeDomino);
}
function cleanupDominos(scope){
  if (scope.matches?.('[data-domino]')) scope.dominoCleanup?.();
  scope.querySelectorAll?.('[data-domino]').forEach(row => row.dominoCleanup?.());
}
initializeDominos(document);
document.addEventListener('shopify:section:load', e => initializeDominos(e.target));
document.addEventListener('shopify:section:unload', e => cleanupDominos(e.target));
document.addEventListener('shopify:section:select', e => {
  e.target.querySelectorAll?.('[data-domino]').forEach(row => row.dominoReveal?.());
});
document.addEventListener('shopify:block:select', e => e.target.closest('[data-domino]')?.dominoReveal?.());

function centerItemInRail(rail, item){
  if (!rail || !item) return;
  const railRect = rail.getBoundingClientRect();
  const itemRect = item.getBoundingClientRect();
  const centerDelta = itemRect.left + (itemRect.width / 2)
    - railRect.left - (railRect.width / 2);
  if (Math.abs(centerDelta) <= 1) return;
  rail.scrollBy({ left: centerDelta, behavior: 'auto' });
}

/* ---------- gallery deck + arc mini-rail (product-media-deck block) ---------- */
function initializeProductDeck(root){
  if (root.productDeckCleanup) return;
  const cards = [...root.querySelectorAll('.gcard')];
  const thumbs = [...root.querySelectorAll('[data-deck-thumbnail]')];
  const previous = root.querySelector('[data-deck-previous]');
  const next = root.querySelector('[data-deck-next]');
  const status = root.querySelector('[data-deck-status]');
  const defaultLabel = root.getAttribute('aria-label');
  if (!cards.length) return;
  const hasMultipleCards = cards.length > 1;
  if (previous) previous.disabled = !hasMultipleCards;
  if (next) next.disabled = !hasMultipleCards;

  if (hasMultipleCards) {
    root.setAttribute('tabindex', '0');
    root.setAttribute('aria-keyshortcuts', 'ArrowLeft ArrowRight Home End');
  }
  if (hasMultipleCards && root.dataset.deckKeyboardLabel) {
    root.setAttribute('aria-label', root.dataset.deckKeyboardLabel);
  }

  let front = cards.findIndex(card => card.dataset.productMediaId === root.dataset.selectedMediaId);
  if (front < 0) front = cards.findIndex(card => card.classList.contains('gcard--c'));
  if (front < 0) front = 0;
  const cardStates = ['gcard--c', 'gcard--l', 'gcard--r', 'gcard--hid'];
  const pauseMedia = (card) => {
    card.querySelector('video')?.pause();
    card.querySelector('model-viewer')?.pause?.();

    const frame = card.querySelector('iframe');
    if (frame?.contentWindow) {
      frame.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
      frame.contentWindow.postMessage(JSON.stringify({ method: 'pause' }), '*');
    }
  };
  const loadDeferredMedia = (card) => {
    if (!card) return null;
    if (card.dataset.deferredMediaLoaded === 'true') {
      return card.querySelector('video[controls], audio[controls], model-viewer, iframe');
    }
    const poster = card.querySelector('[data-deferred-media-poster]');
    const template = card.querySelector('[data-deferred-media-template]');
    if (!poster || !template) return null;

    poster.replaceWith(template.content.cloneNode(true));
    template.remove();
    card.dataset.deferredMediaLoaded = 'true';
    return card.querySelector('video[controls], audio[controls], model-viewer, iframe');
  };
  const deal = () => {
    const count = cards.length;
    cards.forEach((card, index) => {
      const state = index === front ? 'gcard--c' :
        index === (front - 1 + count) % count ? 'gcard--l' :
        index === (front + 1) % count ? 'gcard--r' : 'gcard--hid';
      card.classList.remove(...cardStates);
      card.classList.add(state);
      const isHidden = state === 'gcard--hid';
      card.toggleAttribute('inert', isHidden);
      if (isHidden) card.setAttribute('aria-hidden', 'true');
      else card.removeAttribute('aria-hidden');
      card.querySelectorAll('video, audio, iframe, model-viewer').forEach((control) => {
        control.toggleAttribute('inert', index !== front);
      });
      if (index !== front) pauseMedia(card);
    });
    thumbs.forEach((thumb, index) => {
      const isCurrent = index === front;
      thumb.classList.toggle('on', isCurrent);
      thumb.setAttribute('aria-pressed', String(isCurrent));
    });
    if (status) {
      const message = thumbs[front]?.dataset.deckStatusLabel || '';
      if (message && status.textContent !== message) status.textContent = message;
    }
    if (thumbs.length > 5) {
      const activeThumb = thumbs[front];
      centerItemInRail(activeThumb?.parentElement, activeThumb);
    }
  };

  const cardHandlers = cards.map((card, index) => {
    const handler = (event) => {
      if (event.target.closest('video, iframe, model-viewer')) return;
      if (index === front) return;
      front = index;
      deal();
    };
    card.addEventListener('click', handler);
    return handler;
  });
  const thumbHandlers = thumbs.map((thumb, index) => {
    const handler = () => {
      if (index === front) return;
      front = index;
      deal();
    };
    thumb.addEventListener('click', handler);
    return handler;
  });
  const previousHandler = () => {
    if (!hasMultipleCards) return;
    front = (front - 1 + cards.length) % cards.length;
    deal();
  };
  const nextHandler = () => {
    if (!hasMultipleCards) return;
    front = (front + 1) % cards.length;
    deal();
  };
  const keydownHandler = (event) => {
    if (
      event.defaultPrevented
      || event.isComposing
      || event.altKey
      || event.ctrlKey
      || event.metaKey
      || event.shiftKey
      || !(event.target instanceof Element)
    ) return;

    if (event.target.closest('input, select, textarea, [contenteditable], video, audio, iframe, model-viewer')) return;
    const sourceThumbnail = event.target.closest('[data-deck-thumbnail]');
    if (event.target !== root && !sourceThumbnail) return;
    if (!hasMultipleCards) return;

    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();

    if (event.key === 'ArrowLeft') previousHandler();
    else if (event.key === 'ArrowRight') nextHandler();
    else if (event.key === 'Home' && front !== 0) {
      front = 0;
      deal();
    } else if (event.key === 'End' && front !== cards.length - 1) {
      front = cards.length - 1;
      deal();
    }

    if (sourceThumbnail) thumbs[front]?.focus({ preventScroll: true });
  };
  const deferredMediaHandler = (event) => {
    const poster = event.target.closest?.('[data-deferred-media-poster]');
    if (!poster || !root.contains(poster)) return;
    event.preventDefault();

    const card = poster.closest('.gcard');
    const index = cards.indexOf(card);
    if (index >= 0 && index !== front) {
      front = index;
      deal();
    }
    const loadedControl = loadDeferredMedia(card);
    if (event.detail === 0 && card) {
      loadedControl?.focus?.({ preventScroll: true });
      if (!card.contains(document.activeElement)) root.focus({ preventScroll: true });
    }
  };
  previous?.addEventListener('click', previousHandler);
  next?.addEventListener('click', nextHandler);
  root.addEventListener('click', deferredMediaHandler);
  root.addEventListener('keydown', keydownHandler);

  root.productDeckDeal = deal;
  root.productDeckCleanup = () => {
    cards.forEach((card, index) => card.removeEventListener('click', cardHandlers[index]));
    thumbs.forEach((thumb, index) => thumb.removeEventListener('click', thumbHandlers[index]));
    previous?.removeEventListener('click', previousHandler);
    next?.removeEventListener('click', nextHandler);
    root.removeEventListener('click', deferredMediaHandler);
    root.removeEventListener('keydown', keydownHandler);
    root.removeAttribute('tabindex');
    root.removeAttribute('aria-keyshortcuts');
    if (defaultLabel) root.setAttribute('aria-label', defaultLabel);
    else root.removeAttribute('aria-label');
    cards.forEach((card) => {
      card.querySelectorAll('video, audio, iframe, model-viewer').forEach((control) => {
        control.removeAttribute('inert');
      });
    });
    delete root.productDeckDeal;
    delete root.productDeckCleanup;
  };
  deal();
}
function initializeProductDecks(scope){
  if (scope.matches?.('[data-media-deck]')) initializeProductDeck(scope);
  scope.querySelectorAll?.('[data-media-deck]').forEach(initializeProductDeck);
}
function cleanupProductDecks(scope){
  if (scope.matches?.('[data-media-deck]')) scope.productDeckCleanup?.();
  scope.querySelectorAll?.('[data-media-deck]').forEach(root => root.productDeckCleanup?.());
}
initializeProductDecks(document);
document.addEventListener('shopify:section:load', event => initializeProductDecks(event.target));
document.addEventListener('shopify:section:unload', event => cleanupProductDecks(event.target));
document.addEventListener('shopify:block:select', event => {
  const deck = event.target.closest('[data-media-deck]');
  if (!deck) return;
  initializeProductDeck(deck);
  deck.productDeckDeal?.();
});

/* ---------- optional-motion horizontal rails ---------- */
function initializeMotionRail(root){
  if (root.motionRailCleanup) return;
  const rail = root.querySelector('[data-motion-rail]');
  const controls = root.querySelector('[data-motion-rail-controls]');
  const previous = root.querySelector('[data-motion-rail-prev]');
  const next = root.querySelector('[data-motion-rail-next]');
  if (!rail || !controls || !previous || !next) return;

  let updateFrame = 0;
  const items = () => [...rail.children];
  const railState = cards => {
    const railRect = rail.getBoundingClientRect();
    const cardRects = cards.map(card => card.getBoundingClientRect());
    const isRtl = getComputedStyle(rail).direction === 'rtl';
    const tolerance = 2;
    const firstRect = cardRects[0];
    const lastRect = cardRects[cardRects.length - 1];
    const fullyVisible = cardRects
      .map((rect, index) => ({ index, visible: rect.left >= railRect.left - tolerance && rect.right <= railRect.right + tolerance }))
      .filter(item => item.visible)
      .map(item => item.index);
    return {
      railRect,
      cardRects,
      atStart: isRtl
        ? firstRect.right <= railRect.right + tolerance
        : firstRect.left >= railRect.left - tolerance,
      atEnd: isRtl
        ? lastRect.left >= railRect.left - tolerance
        : lastRect.right <= railRect.right + tolerance,
      firstFullyVisible: fullyVisible[0] ?? -1,
      lastFullyVisible: fullyVisible[fullyVisible.length - 1] ?? -1
    };
  };
  const closestIndex = cards => {
    if (!cards.length) return -1;
    const railRect = rail.getBoundingClientRect();
    const center = railRect.left + railRect.width / 2;
    return cards.reduce((best, card, index) => {
      const rect = card.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - center);
      return distance < best.distance ? { index, distance } : best;
    }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
  };
  const update = () => {
    updateFrame = 0;
    const cards = items();
    const hasOverflow = cards.length > 1 && rail.scrollWidth > rail.clientWidth + 1;
    controls.hidden = !hasOverflow;
    if (!hasOverflow) {
      previous.disabled = true;
      next.disabled = true;
      return;
    }
    const state = railState(cards);
    previous.disabled = state.atStart;
    next.disabled = state.atEnd;
  };
  const queueUpdate = () => {
    if (!updateFrame) updateFrame = requestAnimationFrame(update);
  };
  const move = delta => {
    const cards = items();
    if (!cards.length) return;
    const state = railState(cards);
    if ((delta < 0 && state.atStart) || (delta > 0 && state.atEnd)) return;
    const current = closestIndex(cards);
    const target = delta < 0
      ? (state.firstFullyVisible > 0 ? state.firstFullyVisible - 1 : Math.max(0, current - 1))
      : (state.lastFullyVisible >= 0 && state.lastFullyVisible < cards.length - 1
          ? state.lastFullyVisible + 1
          : Math.min(cards.length - 1, current + 1));
    centerItemInRail(rail, cards[target]);
    queueUpdate();
  };
  const previousClick = () => move(-1);
  const nextClick = () => move(1);
  previous.addEventListener('click', previousClick);
  next.addEventListener('click', nextClick);
  rail.addEventListener('scroll', queueUpdate, { passive: true });
  window.addEventListener('resize', queueUpdate, { passive: true });
  const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(queueUpdate) : null;
  resizeObserver?.observe(rail);

  root.motionRailRefresh = update;
  root.motionRailCleanup = () => {
    previous.removeEventListener('click', previousClick);
    next.removeEventListener('click', nextClick);
    rail.removeEventListener('scroll', queueUpdate);
    window.removeEventListener('resize', queueUpdate);
    resizeObserver?.disconnect();
    if (updateFrame) cancelAnimationFrame(updateFrame);
    controls.hidden = true;
    delete root.motionRailRefresh;
    delete root.motionRailCleanup;
  };
  update();
}
function initializeMotionRails(scope){
  if (scope.matches?.('[data-motion-rail-root]')) initializeMotionRail(scope);
  scope.querySelectorAll?.('[data-motion-rail-root]').forEach(initializeMotionRail);
}
function cleanupMotionRails(scope){
  if (scope.matches?.('[data-motion-rail-root]')) scope.motionRailCleanup?.();
  scope.querySelectorAll?.('[data-motion-rail-root]').forEach(root => root.motionRailCleanup?.());
}
initializeMotionRails(document);
document.addEventListener('shopify:section:load', event => initializeMotionRails(event.target));
document.addEventListener('shopify:section:unload', event => cleanupMotionRails(event.target));
document.addEventListener('shopify:block:select', event => {
  const root = event.target.closest('[data-motion-rail-root]');
  if (!root) return;
  initializeMotionRail(root);
  root.motionRailRefresh?.();
});

/* ---------- quantity selector (quantity-selector block) ---------- */
function initializeQuantitySelector(root){
  if (root.quantityCleanup) return;
  const input = root.querySelector('[data-quantity-input]');
  const minus = root.querySelector('[data-quantity-minus]');
  const plus = root.querySelector('[data-quantity-plus]');
  if (!input || !minus || !plus) return;

  const bounds = () => ({
    min: Number.parseInt(input.min, 10) || 1,
    max: Number.parseInt(input.max, 10) || 999
  });
  const normalize = value => {
    const { min, max } = bounds();
    return Math.min(max, Math.max(min, Number.parseInt(value, 10) || min));
  };
  // aria-disabled keeps keyboard focus on the stepper at a bound (native disabled drops
  // focus to <body>). Clicks at a bound are no-ops: normalize() clamps, and the cart's
  // beginCartQuantityChange() returns early when the quantity is unchanged.
  const setBound = (button, atBound) => {
    button.disabled = false;
    if (atBound) button.setAttribute('aria-disabled', 'true');
    else button.removeAttribute('aria-disabled');
  };
  const sync = value => {
    const nextValue = normalize(value);
    const { min, max } = bounds();
    input.value = nextValue;
    setBound(minus, nextValue <= min);
    setBound(plus, nextValue >= max);
  };
  // Focus stays on +/- after a press, so the product stepper announces the new value through
  // its own status region (the cart stepper has none; the cart page announces its own updates).
  const status = root.querySelector('[data-quantity-status]');
  const label = root.querySelector('label');
  let statusTimer = 0;
  const announce = () => {
    if (!status) return;
    window.clearTimeout(statusTimer);
    statusTimer = window.setTimeout(() => {
      status.textContent = `${label?.textContent.trim() || ''} ${input.value}`.trim();
    }, 250);
  };
  const minusHandler = () => {
    sync(Number(input.value) - 1);
    announce();
  };
  const plusHandler = () => {
    sync(Number(input.value) + 1);
    announce();
  };
  const changeHandler = () => sync(input.value);

  minus.addEventListener('click', minusHandler);
  plus.addEventListener('click', plusHandler);
  input.addEventListener('change', changeHandler);
  root.quantityCleanup = () => {
    minus.removeEventListener('click', minusHandler);
    plus.removeEventListener('click', plusHandler);
    input.removeEventListener('change', changeHandler);
    window.clearTimeout(statusTimer);
    delete root.quantityCleanup;
  };
  sync(input.value);
}
function initializeQuantitySelectors(scope){
  if (scope.matches?.('[data-quantity-selector]')) initializeQuantitySelector(scope);
  scope.querySelectorAll?.('[data-quantity-selector]').forEach(initializeQuantitySelector);
}
function cleanupQuantitySelectors(scope){
  if (scope.matches?.('[data-quantity-selector]')) scope.quantityCleanup?.();
  scope.querySelectorAll?.('[data-quantity-selector]').forEach(root => root.quantityCleanup?.());
}
initializeQuantitySelectors(document);
document.addEventListener('shopify:section:load', event => initializeQuantitySelectors(event.target));
document.addEventListener('shopify:section:unload', event => cleanupQuantitySelectors(event.target));

/* ---------- product add-to-cart + bundled cart badge section ---------- */
const cartIconBubbleSectionId = 'cart-icon-bubble';
const cartDrawerSectionId = 'cart-drawer';
const pendingCartAdds = new Map();
const activeProductAdds = new Map();
let cartAddRequestId = 0;
let cartMutationQueue = Promise.resolve();
let cartDrawerReturnFocus = null;
let cartDrawerReturnFocusSectionId = '';
let cartDrawerFocusToken = 0;

function cartAddSectionIdsFor(drawerSectionId){
  return `${drawerSectionId || cartDrawerSectionId},${cartIconBubbleSectionId}`;
}

function enqueueCartMutation(runMutation){
  cartMutationQueue = cartMutationQueue.then(runMutation, runMutation);
  return cartMutationQueue;
}

function cartItemFromFormData(formData){
  const id = Number(formData.get('id'));
  const quantity = Number(formData.get('quantity') || 1);
  if (!Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(quantity) || quantity <= 0) return null;

  const item = { id, quantity };
  const sellingPlan = Number(formData.get('selling_plan'));
  if (Number.isSafeInteger(sellingPlan) && sellingPlan > 0) item.selling_plan = sellingPlan;

  const properties = {};
  for (const [name, value] of formData.entries()) {
    const propertyMatch = name.match(/^properties\[(.+)\]$/);
    if (!propertyMatch) continue;
    if (typeof File !== 'undefined' && value instanceof File && value.size > 0) return null;
    if (typeof value === 'string' && value !== '') properties[propertyMatch[1]] = value;
  }
  if (Object.keys(properties).length) item.properties = properties;

  return item;
}

function productAddWrapperForRequest(request){
  const currentWrapper = [...document.querySelectorAll('[data-buy-buttons][data-product-add-key]')]
    .find(wrapper => wrapper.dataset.productAddKey === request.key);
  if (currentWrapper) return currentWrapper;
  return request.wrapper?.isConnected ? request.wrapper : null;
}

function productAddWrappersForGroup(groupKey){
  if (!groupKey) return [];
  return [...document.querySelectorAll('[data-buy-buttons][data-product-add-group]')]
    .filter(wrapper => wrapper.dataset.productAddGroup === groupKey);
}

function productPaymentContainersForGroup(groupKey){
  return productAddWrappersForGroup(groupKey)
    .flatMap(wrapper => [...wrapper.querySelectorAll('.shopify-payment-button')]);
}

function productAddUi(request){
  const wrapper = productAddWrapperForRequest(request);
  const form = wrapper?.querySelector('form');
  return {
    wrapper,
    form,
    button: form?.querySelector('[data-product-add]'),
    label: form?.querySelector('[data-product-add-label]'),
    loadingLabel: form?.querySelector('[data-product-add-loading]'),
    feedback: form?.querySelector('[data-product-add-feedback]')
  };
}

function setProductAddLoading(request, busy){
  const ui = productAddUi(request);
  const groupWrappers = productAddWrappersForGroup(request.groupKey);
  const payments = productPaymentContainersForGroup(request.groupKey);
  if (busy && (!ui.wrapper || !ui.form || !ui.button || !ui.label || !ui.loadingLabel)) return false;
  const requestId = String(request.id);

  if (busy) {
    groupWrappers.forEach(wrapper => {
      wrapper.dataset.productAddPending = 'true';
      wrapper.dataset.productAddRequestId = requestId;
    });
    ui.form.dataset.productAddRequestId = requestId;
    ui.form.setAttribute('aria-busy', 'true');

    if (ui.button.dataset.productAddRequestId !== requestId) {
      ui.button.dataset.productAddWasDisabled = ui.button.disabled ? 'true' : 'false';
    }
    ui.button.dataset.productAddRequestId = requestId;
    ui.button.disabled = true;
    ui.button.setAttribute('aria-busy', 'true');
    ui.label.hidden = true;
    ui.loadingLabel.hidden = false;

    payments.forEach(payment => {
      if (payment.dataset.productAddRequestId !== requestId) {
        payment.dataset.productAddWasInert = payment.inert ? 'true' : 'false';
        payment.dataset.productAddPreviousAriaDisabled = payment.getAttribute('aria-disabled') || '';
      }
      payment.dataset.productAddRequestId = requestId;
      payment.inert = true;
      payment.setAttribute('aria-disabled', 'true');
    });
    return true;
  }

  groupWrappers.forEach(wrapper => {
    if (wrapper.dataset.productAddRequestId !== requestId) return;
    delete wrapper.dataset.productAddPending;
    delete wrapper.dataset.productAddRequestId;
  });
  if (ui.form?.dataset.productAddRequestId === requestId) {
    delete ui.form.dataset.productAddRequestId;
    ui.form.removeAttribute('aria-busy');
  }
  if (ui.button?.dataset.productAddRequestId === requestId) {
    ui.button.disabled = ui.button.dataset.productAddWasDisabled === 'true';
    delete ui.button.dataset.productAddWasDisabled;
    delete ui.button.dataset.productAddRequestId;
    ui.button.removeAttribute('aria-busy');
    ui.label.hidden = false;
    ui.loadingLabel.hidden = true;
  }
  payments.forEach(payment => {
    if (payment.dataset.productAddRequestId !== requestId) return;
    payment.inert = payment.dataset.productAddWasInert === 'true';
    const previousAriaDisabled = payment.dataset.productAddPreviousAriaDisabled;
    if (previousAriaDisabled) payment.setAttribute('aria-disabled', previousAriaDisabled);
    else payment.removeAttribute('aria-disabled');
    delete payment.dataset.productAddWasInert;
    delete payment.dataset.productAddPreviousAriaDisabled;
    delete payment.dataset.productAddRequestId;
  });
  return true;
}

function clearProductAddFeedback(request){
  const ui = productAddUi(request);
  if (!ui.wrapper || !ui.feedback) return;
  ui.wrapper.classList.remove('has-product-add-feedback');
  ui.feedback.hidden = true;
  ui.feedback.textContent = '';
}

function showProductAddFeedback(request, message){
  const cleanMessage = typeof message === 'string' ? message.trim() : '';
  const ui = productAddUi(request);
  if (!cleanMessage || !ui.wrapper || !ui.feedback) return false;

  ui.feedback.textContent = cleanMessage;
  ui.feedback.hidden = false;
  ui.wrapper.classList.add('has-product-add-feedback');
  closeCartDrawer(false);
  requestAnimationFrame(() => {
    const currentFeedback = productAddUi(request).feedback;
    if (currentFeedback && !currentFeedback.hidden) currentFeedback.focus({ preventScroll: true });
  });
  return true;
}

function completeProductAdd(request, errorMessage = ''){
  const activeRequest = activeProductAdds.get(request.groupKey);
  if (activeRequest?.id !== request.id) return;
  activeProductAdds.delete(request.groupKey);
  setProductAddLoading(request, false);
  if (errorMessage) showProductAddFeedback(request, errorMessage);
}

function restorePendingProductAddState(scope){
  for (const request of activeProductAdds.values()) {
    const wrapper = productAddWrapperForRequest(request);
    if (wrapper && (scope === document || scope.contains?.(wrapper))) setProductAddLoading(request, true);
  }
}

document.addEventListener('shopify:section:load', event => restorePendingProductAddState(event.target));

function replaceCartIconBubble(sectionHtml){
  if (typeof sectionHtml !== 'string' || !sectionHtml) return false;

  const parsedDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
  const nextBubble = parsedDocument.getElementById(cartIconBubbleSectionId);
  const currentBubble = document.getElementById(cartIconBubbleSectionId);
  if (!nextBubble || !currentBubble) return false;

  currentBubble.replaceWith(document.importNode(nextBubble, true));
  return true;
}

function openCartDrawer(trigger){
  const drawer = document.getElementById(cartDrawerSectionId);
  if (!drawer) return false;

  if (trigger?.isConnected) {
    cartDrawerReturnFocus = trigger;
    cartDrawerReturnFocusSectionId = trigger.closest('[data-main-product]')?.dataset.sectionId || '';
  }
  drawer.removeAttribute('inert');
  drawer.classList.add('is-open');
  drawer.setAttribute('aria-hidden', 'false');
  document.body.classList.add('cart-drawer-open');
  const focusToken = ++cartDrawerFocusToken;
  requestAnimationFrame(() => {
    if (focusToken !== cartDrawerFocusToken || !drawer.isConnected || !drawer.classList.contains('is-open')) return;
    drawer.querySelector('[data-cart-drawer-close-button]')?.focus({ preventScroll: true });
  });
  return true;
}

function closeCartDrawer(restoreFocus = true){
  cartDrawerFocusToken += 1;
  const drawer = document.getElementById(cartDrawerSectionId);
  drawer?.classList.remove('is-open');
  drawer?.setAttribute('aria-hidden', 'true');
  drawer?.setAttribute('inert', '');
  document.body.classList.remove('cart-drawer-open');

  if (restoreFocus) {
    const fallbackFocus = cartDrawerReturnFocusSectionId
      ? document.getElementById(`shopify-section-${cartDrawerReturnFocusSectionId}`)?.querySelector('[data-product-add]')
      : null;
    const focusTarget = cartDrawerReturnFocus?.isConnected ? cartDrawerReturnFocus : fallbackFocus;
    focusTarget?.focus({ preventScroll: true });
  }
  cartDrawerReturnFocus = null;
  cartDrawerReturnFocusSectionId = '';
}

function optimisticCartLine(form, item, requestId){
  const sourceTemplate = form.closest('[data-main-product]')?.querySelector('template[data-cart-optimistic-template]');
  const shellTemplate = document.querySelector('template[data-cart-drawer-optimistic-shell]');
  const source = sourceTemplate?.content.querySelector('[data-cart-optimistic-source]');
  const line = shellTemplate?.content.firstElementChild?.cloneNode(true);
  if (!source || !line) return null;

  const sourceMedia = source.querySelector('[data-cart-source-media]');
  const targetMedia = line.querySelector('[data-cart-drawer-media]');
  if (sourceMedia && targetMedia) {
    targetMedia.replaceChildren(...[...sourceMedia.childNodes].map(node => node.cloneNode(true)));
    targetMedia.querySelector('img')?.classList.add('cart-drawer__image');
    const placeholder = targetMedia.querySelector('[data-cart-source-placeholder]');
    placeholder?.classList.add('cart-drawer__placeholder');
    placeholder?.querySelector('svg')?.classList.add('cart-drawer__placeholder-svg');
  }

  const sourceTitle = source.querySelector('[data-cart-source-title]');
  const targetTitle = line.querySelector('[data-cart-drawer-title]');
  if (sourceTitle && targetTitle) targetTitle.textContent = sourceTitle.textContent;

  const sourceVariant = source.querySelector('[data-cart-source-variant]');
  const targetVariant = line.querySelector('[data-cart-drawer-variant]');
  if (sourceVariant && targetVariant) {
    targetVariant.textContent = sourceVariant.textContent.trim();
    targetVariant.hidden = sourceVariant.hidden;
  }

  const sourcePrice = source.querySelector('[data-cart-source-price]');
  const targetPrice = line.querySelector('[data-cart-drawer-price]');
  if (sourcePrice && targetPrice) targetPrice.replaceChildren(...[...sourcePrice.childNodes].map(node => node.cloneNode(true)));

  line.dataset.cartRequestId = String(requestId);
  const quantity = line.querySelector('[data-cart-drawer-quantity]');
  if (quantity) quantity.textContent = String(item.quantity);
  return line;
}

function insertOptimisticCartLine(request, openDrawer = true){
  const drawer = document.getElementById(cartDrawerSectionId);
  const items = drawer?.querySelector('[data-cart-drawer-items]');
  if (!drawer || !items) return false;

  const alreadyRendered = [...items.querySelectorAll('[data-cart-request-id]')]
    .some(line => line.dataset.cartRequestId === String(request.id));
  if (!alreadyRendered) {
    const emptyState = items.querySelector('[data-cart-drawer-empty]');
    if (emptyState) emptyState.hidden = true;
    items.prepend(request.optimisticLine.cloneNode(true));
  }

  if (openDrawer) openCartDrawer(request.submitter);
  return true;
}

function rollbackOptimisticCartLine(requestId){
  const drawer = document.getElementById(cartDrawerSectionId);
  const items = drawer?.querySelector('[data-cart-drawer-items]');
  if (!items) return;

  [...items.querySelectorAll('[data-cart-request-id]')]
    .find(line => line.dataset.cartRequestId === String(requestId))
    ?.remove();

  if (!items.querySelector('[data-cart-drawer-line]')) {
    const emptyState = items.querySelector('[data-cart-drawer-empty]');
    if (emptyState) emptyState.hidden = false;
    closeCartDrawer();
  }
}

function replaceCartDrawer(sectionHtml){
  if (typeof sectionHtml !== 'string' || !sectionHtml) return false;

  const parsedDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
  const nextDrawer = parsedDocument.getElementById(cartDrawerSectionId);
  const currentDrawer = document.getElementById(cartDrawerSectionId);
  if (!nextDrawer || !currentDrawer) return false;

  const wasOpen = currentDrawer.classList.contains('is-open');
  const importedDrawer = document.importNode(nextDrawer, true);
  if (currentDrawer.dataset.sectionId) importedDrawer.dataset.sectionId = currentDrawer.dataset.sectionId;
  if (currentDrawer.dataset.openOnAdd != null) importedDrawer.dataset.openOnAdd = currentDrawer.dataset.openOnAdd;
  currentDrawer.replaceWith(importedDrawer);
  for (const request of pendingCartAdds.values()) insertOptimisticCartLine(request, false);
  if (wasOpen) openCartDrawer();
  announceCartDomUpdate('cart-drawer');
  return true;
}

function replaceCartSections(sections, drawerSectionId = cartDrawerSectionId){
  const result = { drawer: false, bubble: false };
  if (!sections || typeof sections !== 'object') return result;

  result.bubble = replaceCartIconBubble(sections[cartIconBubbleSectionId]);
  result.drawer = replaceCartDrawer(sections[drawerSectionId] ?? sections[cartDrawerSectionId]);
  return result;
}

async function fetchCartSections(request){
  const sectionUrl = new URL(request.sectionsUrl, window.location.origin);
  sectionUrl.searchParams.set('sections', request.sectionIds);
  // A `?sections=` render must not ask for JSON explicitly: `/cart` would answer with cart state instead.
  const response = await fetch(`${sectionUrl.pathname}${sectionUrl.search}`, {
    credentials: 'same-origin',
    headers: { 'Accept': '*/*' }
  });
  if (!response.ok) throw new Error(`Cart sections request failed: ${response.status}`);
  return response.json();
}

async function reconcileCartFromServer(request, initialSections){
  const result = replaceCartSections(initialSections, request.drawerSectionId);
  if (result.drawer && result.bubble) return result;

  try {
    const fallbackSections = await fetchCartSections(request);
    if (!result.bubble) result.bubble = replaceCartIconBubble(fallbackSections?.[cartIconBubbleSectionId]);
    if (!result.drawer) {
      result.drawer = replaceCartDrawer(
        fallbackSections?.[request.drawerSectionId] ?? fallbackSections?.[cartDrawerSectionId]
      );
    }
  } catch (error) {
    return result;
  }

  return result;
}

function enqueueProductAdd(request){
  const runRequest = async () => {
    let errorMessage = '';
    try {
      const response = await fetch(`${request.shopifyRoot}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [request.item],
          sections: request.sectionIds,
          sections_url: request.sectionsUrl
        })
      });
      let data = {};
      try {
        data = await response.json();
      } catch (error) {
        data = {};
      }

      pendingCartAdds.delete(request.id);
      if (!response.ok) {
        const reconciled = await reconcileCartFromServer(request);
        if (request.optimisticLine && !reconciled.drawer) rollbackOptimisticCartLine(request.id);
        errorMessage = typeof data.description === 'string' && data.description.trim()
          ? data.description.trim()
          : (typeof data.message === 'string' && data.message.trim() ? data.message.trim() : request.errorMessage);
        return;
      }

      const reconciled = await reconcileCartFromServer(request, data.sections);
      if (reconciled.bubble && (!request.openDrawerOnAdd || reconciled.drawer)) {
        if (request.successMessage) window.depotToast?.(request.successMessage);
      } else {
        if (request.optimisticLine && !reconciled.drawer) rollbackOptimisticCartLine(request.id);
        errorMessage = request.unconfirmedMessage;
      }
    } catch (error) {
      pendingCartAdds.delete(request.id);
      const reconciled = await reconcileCartFromServer(request);
      if (request.optimisticLine && !reconciled.drawer) rollbackOptimisticCartLine(request.id);
      errorMessage = request.unconfirmedMessage;
    } finally {
      pendingCartAdds.delete(request.id);
      completeProductAdd(request, errorMessage);
    }
  };

  enqueueCartMutation(runRequest);
}

document.addEventListener('click', event => {
  if (!event.target.closest?.('[data-cart-drawer-close]')) return;
  closeCartDrawer();
});

document.addEventListener('keydown', event => {
  const drawer = document.getElementById(cartDrawerSectionId);
  if (!drawer?.classList.contains('is-open')) return;
  if (event.key === 'Escape') {
    closeCartDrawer();
    return;
  }
  if (event.key !== 'Tab') return;

  const panel = drawer.querySelector('.cart-drawer__panel');
  const focusable = [...(panel?.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  ) || [])].filter(element => element.tabIndex >= 0 && !element.hidden && element.getClientRects().length > 0);
  if (!focusable.length) {
    event.preventDefault();
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement))) {
    event.preventDefault();
    first.focus();
  }
});

document.addEventListener('submit', event => {
  const form = event.target.closest?.('[data-buy-buttons] form');
  if (!form) return;
  const wrapper = form.closest('[data-buy-buttons]');
  const requestKey = wrapper?.dataset.productAddKey || '';
  const requestGroupKey = wrapper?.dataset.productAddGroup || '';
  if (requestGroupKey && activeProductAdds.has(requestGroupKey)) {
    event.preventDefault();
    return;
  }
  const submitter = event.submitter;
  if (!submitter?.matches?.('[data-product-add]') || !requestKey || !requestGroupKey) return;
  if (form.closest('[data-main-product]')?.getAttribute('aria-busy') === 'true') {
    event.preventDefault();
    return;
  }
  if (
    typeof window.fetch !== 'function'
    || typeof window.FormData !== 'function'
    || typeof window.DOMParser !== 'function'
    || typeof window.URL !== 'function'
  ) return;

  const shopifyRoot = window.Shopify?.routes?.root;
  if (typeof shopifyRoot !== 'string' || !shopifyRoot.endsWith('/')) return;

  const item = cartItemFromFormData(new FormData(form));
  if (!item) return;

  const requestId = ++cartAddRequestId;
  const drawer = document.getElementById(cartDrawerSectionId);
  const drawerSectionId = drawer?.dataset.sectionId || cartDrawerSectionId;
  const openDrawerOnAdd = drawer?.dataset.openOnAdd === 'true';
  const optimisticLine = openDrawerOnAdd ? optimisticCartLine(form, item, requestId) : null;
  const hasUiHooks = Boolean(
    form.querySelector('[data-product-add-label]')
    && form.querySelector('[data-product-add-loading]')
    && form.querySelector('[data-product-add-feedback]')
  );
  const drawerCanOpen = Boolean(optimisticLine && drawer?.querySelector('[data-cart-drawer-items]'));
  if (!hasUiHooks || (openDrawerOnAdd && !drawerCanOpen)) return;

  event.preventDefault();
  let request;
  try {
    request = {
      id: requestId,
      key: requestKey,
      groupKey: requestGroupKey,
      item,
      optimisticLine,
      openDrawerOnAdd,
      drawerSectionId,
      sectionIds: cartAddSectionIdsFor(drawerSectionId),
      wrapper,
      sectionsUrl: `${window.location.pathname}${window.location.search}`,
      shopifyRoot,
      submitter,
      successMessage: wrapper.dataset.cartSuccessMessage || '',
      errorMessage: wrapper.dataset.cartErrorMessage || '',
      unconfirmedMessage: wrapper.dataset.cartUnconfirmedMessage || wrapper.dataset.cartErrorMessage || ''
    };
    activeProductAdds.set(request.groupKey, request);
    clearProductAddFeedback(request);
    if (!setProductAddLoading(request, true)) throw new Error('Product add loading UI is unavailable');
    if (request.optimisticLine) {
      pendingCartAdds.set(request.id, request);
      if (!insertOptimisticCartLine(request, request.openDrawerOnAdd)) throw new Error('Cart drawer UI is unavailable');
    }
    enqueueProductAdd(request);
  } catch (error) {
    try {
      if (request) {
        pendingCartAdds.delete(request.id);
        if (request.optimisticLine) rollbackOptimisticCartLine(request.id);
        completeProductAdd(request);
      }
    } finally {
      HTMLFormElement.prototype.submit.call(form);
    }
  }
});

/* ---------- cart page live change + server removal/Undo ---------- */
const cartItemsSectionId = 'main-cart-items';
const cartFooterSectionId = 'main-cart-footer';
const cartHeaderSectionId = 'header';
const cartPageSectionIds = 'main-cart-items,main-cart-footer,header';
let cartPageBusy = false;
let cartPageReloading = false;
let cartPageFocusToken = 0;
let cartUndoFocusToken = 0;
let cartAnnouncementToken = 0;

function announceCartPage(message){
  const cleanMessage = typeof message === 'string' ? message.trim() : '';
  const region = document.querySelector(`#${cartItemsSectionId} [data-cart-live-region]`);
  if (!cleanMessage || !region) return false;

  const announcementToken = ++cartAnnouncementToken;
  region.textContent = '';
  requestAnimationFrame(() => {
    if (announcementToken !== cartAnnouncementToken) return;
    const currentRegion = document.querySelector(`#${cartItemsSectionId} [data-cart-live-region]`);
    if (currentRegion) currentRegion.textContent = cleanMessage;
  });
  return true;
}

function setCartPageBusy(busy){
  if (cartPageReloading && !busy) return;
  cartPageBusy = busy;
  [cartItemsSectionId, cartFooterSectionId].forEach(sectionId => {
    const region = document.getElementById(sectionId);
    if (!region) return;
    region.inert = busy;
    if (busy) region.setAttribute('aria-busy', 'true');
    else region.removeAttribute('aria-busy');
  });
}

function captureCartNote(){
  const note = document.querySelector(`#${cartItemsSectionId} [name="note"]`);
  if (!note) return null;
  return {
    value: note.value,
    focused: document.activeElement === note,
    selectionStart: note.selectionStart,
    selectionEnd: note.selectionEnd
  };
}

function restoreCartNote(snapshot){
  if (!snapshot) return;
  const note = document.querySelector(`#${cartItemsSectionId} [name="note"]`);
  if (!note) return;
  note.value = snapshot.value;
  if (!snapshot.focused) return;
  note.focus({ preventScroll: true });
  if (Number.isInteger(snapshot.selectionStart) && Number.isInteger(snapshot.selectionEnd)) {
    note.setSelectionRange(snapshot.selectionStart, snapshot.selectionEnd);
  }
}

function cartRegionFromHtml(sectionHtml, regionId){
  if (typeof sectionHtml !== 'string' || !sectionHtml) return null;
  const parsedDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
  return parsedDocument.getElementById(regionId);
}

function prepareCartItemsRegion(sectionHtml){
  const nextRegion = cartRegionFromHtml(sectionHtml, cartItemsSectionId);
  const currentRegion = document.getElementById(cartItemsSectionId);
  if (!nextRegion || !currentRegion) return null;

  const noteSnapshot = captureCartNote();
  const configuredHeading = currentRegion.querySelector('.cartpg__heading')?.cloneNode(true);
  const configuredSubheading = currentRegion.querySelector('.cartpg__subheading')?.cloneNode(true);
  const configuredCartNote = currentRegion.querySelector('.cart-note-field')?.cloneNode(true);
  const configuredSectionId = currentRegion.dataset.sectionId || '';
  const importedRegion = document.importNode(nextRegion, true);

  const nextHeading = importedRegion.querySelector('.cartpg__heading');
  const nextSubheading = importedRegion.querySelector('.cartpg__subheading');
  if (configuredHeading && nextHeading) nextHeading.replaceWith(configuredHeading);
  if (configuredSubheading) {
    const heading = importedRegion.querySelector('.cartpg__heading');
    if (nextSubheading) nextSubheading.replaceWith(configuredSubheading);
    else heading?.insertAdjacentElement('afterend', configuredSubheading);
  } else {
    nextSubheading?.remove();
  }
  const nextCartNote = importedRegion.querySelector('.cart-note-field');
  if (configuredCartNote && nextCartNote) nextCartNote.replaceWith(configuredCartNote);
  else if (!configuredCartNote) nextCartNote?.remove();
  if (configuredSectionId) importedRegion.dataset.sectionId = configuredSectionId;

  return { currentRegion, importedRegion, noteSnapshot };
}

function prepareCartFooterRegion(sectionHtml){
  const nextRegion = cartRegionFromHtml(sectionHtml, cartFooterSectionId);
  const currentRegion = document.getElementById(cartFooterSectionId);
  if (!nextRegion || !currentRegion) return null;

  const currentMeter = currentRegion.querySelector('[data-free-shipping-threshold]');
  const nextMeter = nextRegion.querySelector('[data-free-shipping-threshold]');
  const nextCartIsEmpty = !nextRegion.querySelector('[data-cart-footer]');
  const configuredSectionId = currentRegion.dataset.sectionId || '';
  const configuredBlockTypes = currentRegion.dataset.cartBlockTypes || '';
  const blockTypesChanged = currentRegion.dataset.cartBlockTypes !== nextRegion.dataset.cartBlockTypes;
  if (!nextCartIsEmpty && blockTypesChanged) return null;
  if (!nextCartIsEmpty && currentMeter?.dataset.freeShippingThreshold !== nextMeter?.dataset.freeShippingThreshold) {
    return null;
  }

  const configuredServiceRow = currentRegion.querySelector('[data-cart-service-row]')?.cloneNode(true);
  const configuredContinueUrl = currentRegion.querySelector('[data-cart-continue-link]')?.getAttribute('href') || '';
  const importedRegion = document.importNode(nextRegion, true);
  if (configuredServiceRow) {
    const nextServiceRow = importedRegion.querySelector('[data-cart-service-row]');
    if (nextServiceRow) nextServiceRow.replaceWith(configuredServiceRow);
    else importedRegion.querySelector('.csummary__total')?.insertAdjacentElement('beforebegin', configuredServiceRow);
  }
  const nextContinueLink = importedRegion.querySelector('[data-cart-continue-link]');
  if (configuredContinueUrl && nextContinueLink) nextContinueLink.setAttribute('href', configuredContinueUrl);
  if (configuredSectionId) importedRegion.dataset.sectionId = configuredSectionId;
  if (configuredBlockTypes) importedRegion.dataset.cartBlockTypes = configuredBlockTypes;

  return { currentRegion, importedRegion };
}

function prepareCartBubbleRegion(sectionHtml){
  const nextRegion = cartRegionFromHtml(sectionHtml, cartIconBubbleSectionId);
  const currentRegion = document.getElementById(cartIconBubbleSectionId);
  if (!nextRegion || !currentRegion) return null;
  return {
    currentRegion,
    importedRegion: document.importNode(nextRegion, true)
  };
}

function prepareCartPageCommit(sections){
  if (!sections || typeof sections !== 'object') return null;
  const items = prepareCartItemsRegion(sections[cartItemsSectionId]);
  const footer = prepareCartFooterRegion(sections[cartFooterSectionId]);
  const bubble = prepareCartBubbleRegion(sections[cartHeaderSectionId]);
  if (!items || !footer || !bubble) return null;
  return { items, footer, bubble };
}

function commitCartPage(prepared){
  if (!prepared) return false;
  const rootsAreCurrent = (
    prepared.items.currentRegion.isConnected
    && prepared.footer.currentRegion.isConnected
    && prepared.bubble.currentRegion.isConnected
    && document.getElementById(cartItemsSectionId) === prepared.items.currentRegion
    && document.getElementById(cartFooterSectionId) === prepared.footer.currentRegion
    && document.getElementById(cartIconBubbleSectionId) === prepared.bubble.currentRegion
  );
  if (!rootsAreCurrent) return false;

  cleanupQuantitySelectors(prepared.items.currentRegion);
  prepared.items.currentRegion.replaceWith(prepared.items.importedRegion);
  prepared.footer.currentRegion.replaceWith(prepared.footer.importedRegion);
  prepared.bubble.currentRegion.replaceWith(prepared.bubble.importedRegion);
  initializeQuantitySelectors(prepared.items.importedRegion);
  restoreCartNote(prepared.items.noteSnapshot);
  setCartPageBusy(cartPageBusy);
  announceCartDomUpdate('cart-page');
  return true;
}

// App embeds (gang sheet builders, upsells, ...) decorate cart lines and observe the cart roots that
// existed at page load. Replacing a cart region drops those roots, so announce the new markup with the
// `cart:updated` event most apps listen for.
function announceCartDomUpdate(source){
  try {
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { source } }));
  } catch (error) {
    // A listener error must never break the cart flow.
  }
}

function cartPageResult(complete = false, reloading = false){
  return {
    items: complete,
    footer: complete,
    header: complete,
    complete,
    reloading
  };
}

function forceCartPageReload(shopifyRoot){
  if (cartPageReloading) return true;
  cartPageReloading = true;
  cartPageFocusToken += 1;
  cartUndoFocusToken += 1;
  setCartPageBusy(true);

  if (typeof shopifyRoot === 'string' && shopifyRoot.endsWith('/')) {
    const cartUrl = new URL(`${shopifyRoot}cart`, window.location.origin);
    window.location.assign(cartUrl.href);
  } else {
    window.location.reload();
  }
  return true;
}

function mergeCartPageSections(currentSections, nextSections){
  const merged = { ...currentSections };
  if (!nextSections || typeof nextSections !== 'object') return merged;
  [cartItemsSectionId, cartFooterSectionId, cartHeaderSectionId].forEach(sectionId => {
    if (typeof nextSections[sectionId] === 'string' && nextSections[sectionId]) {
      merged[sectionId] = nextSections[sectionId];
    }
  });
  return merged;
}

async function completeCartPageCommit(prepared, animateLayout = false){
  if (!prepared) return cartPageResult();
  const committed = animateLayout
    ? await runSignatureViewTransition(() => commitCartPage(prepared), 'cart')
    : commitCartPage(prepared);
  if (!committed) return cartPageResult();
  return cartPageResult(true);
}

async function prepareConfiguredCartPageCommit(shopifyRoot, sections){
  let prepared = prepareCartPageCommit(sections);
  if (prepared) return prepared;

  const hasItems = typeof sections?.[cartItemsSectionId] === 'string' && sections[cartItemsSectionId];
  const hasHeader = typeof sections?.[cartHeaderSectionId] === 'string' && sections[cartHeaderSectionId];
  if (!hasItems || !hasHeader) return null;

  try {
    const configuredSections = await fetchConfiguredCartPageSections(shopifyRoot);
    if (!configuredSections) return null;
    prepared = prepareCartPageCommit(configuredSections);
  } catch (error) {
    return null;
  }
  return prepared;
}

async function fetchCartPageSections(shopifyRoot){
  const sectionUrl = new URL(`${shopifyRoot}cart`, window.location.origin);
  sectionUrl.searchParams.set('sections', cartPageSectionIds);
  // `/cart` with `Accept: application/json` is answered like `/cart.js` (cart state, no sections),
  // so the section render request must not ask for JSON explicitly.
  const response = await fetch(`${sectionUrl.pathname}${sectionUrl.search}`, {
    credentials: 'same-origin',
    headers: { 'Accept': '*/*' }
  });
  if (!response.ok) throw new Error(`Cart page sections request failed: ${response.status}`);
  return response.json();
}

async function fetchConfiguredCartPageSections(shopifyRoot){
  const currentRegion = document.getElementById(cartFooterSectionId);
  const sectionId = currentRegion?.dataset.sectionId;
  if (!sectionId || sectionId === cartFooterSectionId) return null;

  const sectionUrl = new URL(`${shopifyRoot}cart`, window.location.origin);
  sectionUrl.searchParams.set('sections', `${cartItemsSectionId},${sectionId},${cartHeaderSectionId}`);
  // See fetchCartPageSections: never request `/cart?sections=` with `Accept: application/json`.
  const response = await fetch(`${sectionUrl.pathname}${sectionUrl.search}`, {
    credentials: 'same-origin',
    headers: { 'Accept': '*/*' }
  });
  if (!response.ok) return null;
  const sections = await response.json();
  return {
    [cartItemsSectionId]: sections?.[cartItemsSectionId],
    [cartFooterSectionId]: sections?.[sectionId],
    [cartHeaderSectionId]: sections?.[cartHeaderSectionId]
  };
}

async function reconcileCartPage(shopifyRoot, initialSections, animateLayout = false){
  let candidateSections = mergeCartPageSections({}, initialSections);
  let prepared = await prepareConfiguredCartPageCommit(shopifyRoot, candidateSections);
  let result = await completeCartPageCommit(prepared, animateLayout);
  if (result.complete) return result;

  try {
    const fallbackSections = await fetchCartPageSections(shopifyRoot);
    candidateSections = mergeCartPageSections({}, fallbackSections);
    prepared = await prepareConfiguredCartPageCommit(shopifyRoot, candidateSections);
    result = await completeCartPageCommit(prepared, animateLayout);
  } catch (error) {
    return cartPageResult();
  }
  return result;
}

async function fetchAuthoritativeCart(shopifyRoot){
  const response = await fetch(`${shopifyRoot}cart.js`, {
    credentials: 'same-origin',
    headers: { 'Accept': 'application/json' }
  });
  if (!response.ok) throw new Error(`Cart state request failed: ${response.status}`);
  const cartState = await response.json();
  if (!Array.isArray(cartState?.items)) throw new Error('Cart state response is invalid');
  return cartState;
}

function stableCartValue(value){
  if (Array.isArray(value)) return value.map(stableCartValue);
  if (value && typeof value === 'object') {
    return Object.keys(value).sort().reduce((result, key) => {
      result[key] = stableCartValue(value[key]);
      return result;
    }, {});
  }
  return value == null ? '' : String(value);
}

function cartPropertiesSignature(properties){
  // Liquid serializes line properties as pairs; Ajax returns the same properties as an object.
  if (Array.isArray(properties)) properties = Object.fromEntries(properties);
  return JSON.stringify(stableCartValue(properties && typeof properties === 'object' ? properties : {}));
}

// Liquid HTML-escapes line item property keys and values (they are shopper input), so the
// `data-line-properties` JSON carries `&amp;`, `&quot;`, `&#39;`... while `/cart.js` returns the raw
// text. Decode once so both sides describe the same line.
let cartPropertyTextDecoder = null;
function decodeCartPropertyText(value){
  if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
  cartPropertyTextDecoder ||= document.createElement('textarea');
  cartPropertyTextDecoder.innerHTML = value;
  return cartPropertyTextDecoder.value;
}

function decodeCartProperties(properties){
  if (Array.isArray(properties)) {
    return properties.map(entry => (Array.isArray(entry) ? entry.map(decodeCartPropertyText) : decodeCartPropertyText(entry)));
  }
  if (properties && typeof properties === 'object') {
    return Object.keys(properties).reduce((result, key) => {
      result[decodeCartPropertyText(key)] = decodeCartPropertyText(properties[key]);
      return result;
    }, {});
  }
  return properties;
}

function cartLineIdentityFromElement(line){
  if (!line) return null;
  const variantId = String(line.dataset.variantId || '');
  if (!variantId) return null;
  let properties = {};
  try {
    properties = decodeCartProperties(JSON.parse(line.dataset.lineProperties || '{}'));
  } catch (error) {
    return null;
  }
  return {
    variantId,
    sellingPlanId: String(line.dataset.sellingPlanId || ''),
    properties: cartPropertiesSignature(properties)
  };
}

function cartLineIdentityFromItem(item){
  if (!item) return null;
  return {
    variantId: String(item.variant_id || ''),
    sellingPlanId: String(item.selling_plan_allocation?.selling_plan?.id || ''),
    properties: cartPropertiesSignature(item.properties)
  };
}

function cartLineIdentitiesMatch(first, second){
  return Boolean(
    first
    && second
    && first.variantId === second.variantId
    && first.sellingPlanId === second.sellingPlanId
    && first.properties === second.properties
  );
}

function resolveAuthoritativeCartLine(transaction, cartState){
  const items = cartState.items || [];
  const exactKeyMatches = items.filter(item => (
    String(item.key) === transaction.sourceKey
    && cartLineIdentitiesMatch(transaction.identity, cartLineIdentityFromItem(item))
  ));
  if (exactKeyMatches.length === 1) return exactKeyMatches[0];

  // Shopify derives the line key from the variant and its properties, so a unique key match is the
  // same line even when the rendered property signature could not be compared.
  const keyMatches = items.filter(item => String(item.key) === transaction.sourceKey);
  if (keyMatches.length === 1) return keyMatches[0];

  const identityMatches = items.filter(item => (
    cartLineIdentitiesMatch(transaction.identity, cartLineIdentityFromItem(item))
  ));
  return identityMatches.length === 1 ? identityMatches[0] : null;
}

function cartDomLineForTransaction(transaction, resolvedKey){
  const lines = [...document.querySelectorAll(`#${cartItemsSectionId} [data-cart-line]`)];
  const keyMatch = lines.find(line => (
    (line.dataset.lineKey === resolvedKey || line.dataset.lineKey === transaction.sourceKey)
    && cartLineIdentitiesMatch(transaction.identity, cartLineIdentityFromElement(line))
  ));
  if (keyMatch) return keyMatch;

  const keyOnlyMatches = lines.filter(line => line.dataset.lineKey === resolvedKey);
  if (keyOnlyMatches.length === 1) return keyOnlyMatches[0];

  const identityMatches = lines.filter(line => (
    cartLineIdentitiesMatch(transaction.identity, cartLineIdentityFromElement(line))
  ));
  return identityMatches.length === 1 ? identityMatches[0] : null;
}

async function acquireAuthoritativeCartLine(transaction, shopifyRoot){
  const cartState = await fetchAuthoritativeCart(shopifyRoot);
  const item = resolveAuthoritativeCartLine(transaction, cartState);
  if (!item) return null;
  const line = cartDomLineForTransaction(transaction, String(item.key));
  if (!line) return null;
  return { cartState, item, line };
}

function cartAddItemFromCartItem(item){
  const id = Number(item?.variant_id);
  const quantity = Number.parseInt(item?.quantity, 10);
  if (!Number.isSafeInteger(id) || id <= 0 || !Number.isInteger(quantity) || quantity < 1) return null;

  const addItem = { id, quantity };
  if (item.properties && typeof item.properties === 'object' && !Array.isArray(item.properties)) {
    addItem.properties = { ...item.properties };
  }
  const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id;
  if (sellingPlanId != null && String(sellingPlanId) !== '') addItem.selling_plan = sellingPlanId;
  return addItem;
}

function cartRestorePlanForLine(cartState, item){
  const cartItems = Array.isArray(cartState?.items) ? cartState.items : [];
  const sourceKey = String(item?.key || '');
  const targetItem = cartAddItemFromCartItem(item);
  if (!sourceKey || !targetItem) return null;

  const parentKey = String(item.parent_relationship?.parent_key || '');
  if (parentKey) {
    const parentItem = cartItems.find(candidate => String(candidate.key) === parentKey);
    const parentIdentity = cartLineIdentityFromItem(parentItem);
    if (!parentItem || !parentIdentity) return null;
    return {
      items: [targetItem],
      parent: { sourceKey: parentKey, identity: parentIdentity }
    };
  }

  const items = [targetItem];
  const childItems = cartItems.filter(candidate => (
    String(candidate.parent_relationship?.parent_key || '') === sourceKey
  ));
  for (const childItem of childItems) {
    const childAddItem = cartAddItemFromCartItem(childItem);
    if (!childAddItem) return null;
    childAddItem.parent_id = targetItem.id;
    items.push(childAddItem);
  }
  return { items, parent: null };
}

function cartIdentityQuantity(cartState, identity){
  return (cartState?.items || []).reduce((quantity, item) => {
    if (!cartLineIdentitiesMatch(identity, cartLineIdentityFromItem(item))) return quantity;
    const itemQuantity = Number.parseInt(item.quantity, 10);
    return quantity + (Number.isInteger(itemQuantity) && itemQuantity > 0 ? itemQuantity : 0);
  }, 0);
}

async function cartRestoreItemsForTransaction(transaction, shopifyRoot, currentCartState = null){
  const restorePlan = transaction.restorePlan;
  if (!restorePlan?.items?.length) return null;
  const items = restorePlan.items.map(item => ({
    ...item,
    ...(item.properties ? { properties: { ...item.properties } } : {})
  }));
  if (!restorePlan.parent) return items;

  const cartState = currentCartState || await fetchAuthoritativeCart(shopifyRoot);
  const parentItem = resolveAuthoritativeCartLine(restorePlan.parent, cartState);
  if (!parentItem) return null;
  items[0].parent_line_key = String(parentItem.key);
  return items;
}

function cartLineIdentityFromAddItem(item){
  if (!item) return null;
  return {
    variantId: String(item.id || ''),
    sellingPlanId: String(item.selling_plan || ''),
    properties: cartPropertiesSignature(item.properties)
  };
}

function cartRestorationWasApplied(beforeCart, afterCart, restoreItems){
  const expectations = new Map();
  for (const restoreItem of restoreItems) {
    const identity = cartLineIdentityFromAddItem(restoreItem);
    const quantity = Number.parseInt(restoreItem.quantity, 10);
    if (!identity?.variantId || !Number.isInteger(quantity) || quantity < 1) return false;
    const key = JSON.stringify(identity);
    const current = expectations.get(key) || { identity, quantity: 0 };
    current.quantity += quantity;
    expectations.set(key, current);
  }
  return [...expectations.values()].every(expectation => (
    cartIdentityQuantity(afterCart, expectation.identity)
      >= cartIdentityQuantity(beforeCart, expectation.identity) + expectation.quantity
  ));
}

async function changeCartLine(lineKey, quantity, shopifyRoot){
  let result = cartPageResult();
  let mutationAccepted = false;
  let errorMessage = '';
  try {
    const response = await fetch(`${shopifyRoot}cart/change.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: String(lineKey),
        quantity,
        sections: cartPageSectionIds,
        sections_url: `${shopifyRoot}cart`
      })
    });
    let data = {};
    try {
      data = await response.json();
    } catch (error) {
      data = {};
    }
    mutationAccepted = response.ok;
    if (!response.ok) {
      if (typeof data.description === 'string' && data.description.trim()) errorMessage = data.description.trim();
      else if (typeof data.message === 'string' && data.message.trim()) errorMessage = data.message.trim();
    }
    result = await reconcileCartPage(shopifyRoot, response.ok ? data.sections : undefined);
  } catch (error) {
    result = await reconcileCartPage(shopifyRoot);
  }

  if (!result.complete) {
    forceCartPageReload(shopifyRoot);
    return { ...result, mutationAccepted, errorMessage, reloading: true };
  }
  return { ...result, mutationAccepted, errorMessage };
}

async function addCartItems(items, shopifyRoot, animateLayout = false){
  let result = cartPageResult();
  let mutationAccepted = false;
  try {
    const response = await fetch(`${shopifyRoot}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        items,
        sections: cartPageSectionIds,
        sections_url: `${shopifyRoot}cart`
      })
    });
    let data = {};
    try {
      data = await response.json();
    } catch (error) {
      data = {};
    }
    mutationAccepted = response.ok;
    result = await reconcileCartPage(
      shopifyRoot,
      response.ok ? data.sections : undefined,
      animateLayout && response.ok
    );
  } catch (error) {
    result = await reconcileCartPage(shopifyRoot);
  }

  if (!result.complete) {
    forceCartPageReload(shopifyRoot);
    return { ...result, mutationAccepted, reloading: true };
  }
  return { ...result, mutationAccepted };
}

function cartLineAtIndex(lineIndex){
  return [...document.querySelectorAll(`#${cartItemsSectionId} [data-cart-line]`)]
    .find(line => Number.parseInt(line.dataset.lineIndex, 10) === lineIndex);
}

function focusCartTransaction(transaction){
  const focusToken = ++cartPageFocusToken;
  requestAnimationFrame(() => {
    if (focusToken !== cartPageFocusToken || cartPageBusy || cartPageReloading) return;
    const identityMatches = [...document.querySelectorAll(`#${cartItemsSectionId} [data-cart-line]`)]
      .filter(line => cartLineIdentitiesMatch(transaction.identity, cartLineIdentityFromElement(line)));
    const line = identityMatches.length === 1
      ? identityMatches[0]
      : (cartLineAtIndex(transaction.lineIndex) || cartLineAtIndex(transaction.lineIndex - 1));
    let focusTarget = null;
    if (line) {
      if (transaction.focusKind === 'minus') focusTarget = line.querySelector('[data-quantity-minus]');
      else if (transaction.focusKind === 'plus') focusTarget = line.querySelector('[data-quantity-plus]');
      else if (transaction.focusKind === 'remove') focusTarget = line.querySelector('[data-remove]');
      else focusTarget = line.querySelector('[data-quantity-input]');
    }
    focusTarget ||= document.querySelector(`#${cartItemsSectionId} [data-cart-line] [data-quantity-input]`);
    focusTarget ||= document.querySelector(`#${cartItemsSectionId} .cartpg__heading`);
    focusTarget?.focus({ preventScroll: true });
  });
}

function cartFocusKind(control){
  if (control.matches('[data-quantity-minus]')) return 'minus';
  if (control.matches('[data-quantity-plus]')) return 'plus';
  if (control.matches('[data-remove]')) return 'remove';
  return 'input';
}

function cartShopifyRoot(){
  const shopifyRoot = window.Shopify?.routes?.root;
  return typeof shopifyRoot === 'string' && shopifyRoot.endsWith('/') ? shopifyRoot : '';
}

async function reconcileCartPageOrReload(shopifyRoot, initialSections){
  const result = await reconcileCartPage(shopifyRoot, initialSections);
  if (result.complete) return result;
  forceCartPageReload(shopifyRoot);
  return { ...result, reloading: true };
}

// App-owned cart mutations use the same authoritative section refresh as theme controls.
document.addEventListener('gsb:cart-updated', () => {
  enqueueCartMutation(async () => {
    const shopifyRoot = cartShopifyRoot();
    if (!shopifyRoot) throw new Error('Shopify cart root is unavailable');

    if (document.getElementById(cartItemsSectionId)) {
      setCartPageBusy(true);
      try {
        await reconcileCartPageOrReload(shopifyRoot);
      } finally {
        setCartPageBusy(false);
      }
      return;
    }

    const drawer = document.getElementById(cartDrawerSectionId);
    const drawerSectionId = drawer?.dataset.sectionId || cartDrawerSectionId;
    const result = await reconcileCartFromServer({
      drawerSectionId,
      sectionIds: cartAddSectionIdsFor(drawerSectionId),
      sectionsUrl: `${window.location.pathname}${window.location.search}`
    });
    if (!result.bubble || (drawer && !result.drawer)) {
      throw new Error('App cart update could not refresh the theme sections');
    }
  }).catch(error => console.error('[Depot cart] App cart refresh failed', error));
});

function focusCartUndoAction(isDecisionPending){
  const focusToken = ++cartUndoFocusToken;
  requestAnimationFrame(() => {
    if (focusToken !== cartUndoFocusToken || cartPageReloading || !isDecisionPending()) return;
    const toast = document.getElementById('toast');
    const action = toast?.querySelector('[data-toast-action]');
    if (!toast?.classList.contains('show') || !action || action.hidden) return;
    action.focus({ preventScroll: true });
  });
}

function beginCartQuantityChange(control){
  if (cartPageBusy) return;
  const line = control.closest('[data-cart-line]');
  const cartRegion = control.closest(`#${cartItemsSectionId}`);
  const input = line?.querySelector('[data-quantity-input]');
  const sourceKey = line?.dataset.lineKey;
  const identity = cartLineIdentityFromElement(line);
  const lineIndex = Number.parseInt(line?.dataset.lineIndex, 10);
  const previousQuantity = Number.parseInt(line?.dataset.cartQuantity, 10);
  const requestedQuantity = Number.parseInt(input?.value, 10);
  if (!cartRegion || !sourceKey || !identity || !Number.isInteger(lineIndex) || !Number.isInteger(previousQuantity) || !Number.isInteger(requestedQuantity) || requestedQuantity < 1) return;
  if (requestedQuantity === previousQuantity) return;
  // Only a real change may close (or commit) an open toast; a click on a bound-reached
  // aria-disabled stepper button clamps to the same value and returns above untouched.
  closeDepotToast('replace');

  const transaction = {
    sourceKey,
    identity,
    lineIndex,
    previousQuantity,
    requestedQuantity,
    quantityDelta: requestedQuantity - previousQuantity,
    focusKind: cartFocusKind(control),
    errorMessage: cartRegion.dataset.cartErrorMessage || '',
    updatedMessage: cartRegion.dataset.cartUpdatedMessage || ''
  };
  const shopifyRoot = cartShopifyRoot();
  if (!shopifyRoot) {
    forceCartPageReload(shopifyRoot);
    return;
  }

  setCartPageBusy(true);
  enqueueCartMutation(async () => {
    try {
      const authoritativeLine = await acquireAuthoritativeCartLine(transaction, shopifyRoot);
      if (!authoritativeLine) {
        forceCartPageReload(shopifyRoot);
        return;
      }

      const serverQuantity = Number.parseInt(authoritativeLine.item.quantity, 10);
      const freshInput = authoritativeLine.line.querySelector('[data-quantity-input]');
      if (!Number.isInteger(serverQuantity) || !freshInput) {
        forceCartPageReload(shopifyRoot);
        return;
      }
      const minimum = Number.parseInt(freshInput.min, 10) || 1;
      const maximum = Number.parseInt(freshInput.max, 10) || 999;
      const isStepChange = transaction.focusKind === 'minus' || transaction.focusKind === 'plus';
      const intendedQuantity = isStepChange
        ? serverQuantity + transaction.quantityDelta
        : transaction.requestedQuantity;
      const nextQuantity = Math.min(maximum, Math.max(minimum, intendedQuantity));

      if (nextQuantity === serverQuantity) {
        await reconcileCartPageOrReload(shopifyRoot);
        return;
      }
      const result = await changeCartLine(String(authoritativeLine.item.key), nextQuantity, shopifyRoot);
      if (!result.reloading) {
        if (result.mutationAccepted) announceCartPage(transaction.updatedMessage);
        else {
          const message = result.errorMessage || transaction.errorMessage;
          if (message) window.depotToast?.(message);
        }
      }
    } catch (error) {
      forceCartPageReload(shopifyRoot);
    } finally {
      if (!cartPageReloading) {
        setCartPageBusy(false);
        focusCartTransaction(transaction);
      }
    }
  });
}

function showCartMutationError(transaction){
  if (transaction.errorMessage) window.depotToast?.(transaction.errorMessage);
  focusCartTransaction(transaction);
}

function showCartRemovalUndo(transaction, shopifyRoot){
  let removalToast = null;
  let decisionSettled = false;

  const dismissUndo = () => {
    if (decisionSettled) return;
    const toastControl = document.activeElement?.closest?.('#toast [data-toast-action], #toast [data-toast-close]');
    const shouldRestoreFocus = Boolean(toastControl);
    decisionSettled = true;
    cartUndoFocusToken += 1;
    transaction.focusKind = 'input';
    removalToast?.close?.();
    if (shouldRestoreFocus) focusCartTransaction(transaction);
  };

  const restoreRemovedItems = () => {
    if (decisionSettled) return;
    decisionSettled = true;
    cartUndoFocusToken += 1;
    const toastMessage = document.querySelector('#toast [data-toast-message]');
    if (toastMessage && transaction.restoringMessage) toastMessage.textContent = transaction.restoringMessage;
    setCartPageBusy(true);

    enqueueCartMutation(async () => {
      setCartPageBusy(true);
      let restored = false;
      try {
        const beforeCart = await fetchAuthoritativeCart(shopifyRoot);
        const restoreItems = await cartRestoreItemsForTransaction(transaction, shopifyRoot, beforeCart);
        if (!restoreItems) throw new Error('Cart restore payload is unavailable');

        const result = await addCartItems(restoreItems, shopifyRoot, true);
        if (result.reloading) return;
        restored = result.mutationAccepted;
        if (!restored) {
          const afterCart = await fetchAuthoritativeCart(shopifyRoot);
          restored = cartRestorationWasApplied(beforeCart, afterCart, restoreItems);
        }
        if (restored) transaction.focusKind = 'remove';
      } catch (error) {
        restored = false;
      } finally {
        if (!cartPageReloading) {
          removalToast?.close?.();
          setCartPageBusy(false);
          if (restored) {
            announceCartPage(transaction.updatedMessage);
            focusCartTransaction(transaction);
          }
          else showCartMutationError(transaction);
        }
      }
    });
  };

  removalToast = window.depotToast?.(transaction.removedMessage, {
    actionLabel: transaction.undoLabel,
    persistent: true,
    dismissible: true,
    deferCloseUntilResolved: true,
    onAction: restoreRemovedItems,
    onDismiss: dismissUndo
  });
  if (removalToast) focusCartUndoAction(() => !decisionSettled);
  else restoreRemovedItems();
}

function beginCartRemoval(removeLink){
  if (cartPageBusy) return;
  closeDepotToast('replace');
  const cartRegion = removeLink.closest(`#${cartItemsSectionId}`);
  const line = removeLink.closest('[data-cart-line]');
  const sourceKey = line?.dataset.lineKey;
  const identity = cartLineIdentityFromElement(line);
  const lineIndex = Number.parseInt(line?.dataset.lineIndex, 10);
  if (!cartRegion || !line || !sourceKey || !identity || !Number.isInteger(lineIndex)) return;

  const transaction = {
    sourceKey,
    identity,
    lineIndex,
    focusKind: 'remove',
    removedMessage: cartRegion.dataset.cartRemovedMessage || '',
    undoLabel: cartRegion.dataset.cartUndoLabel || '',
    restoringMessage: cartRegion.dataset.cartRestoringMessage || '',
    errorMessage: cartRegion.dataset.cartErrorMessage || '',
    updatedMessage: cartRegion.dataset.cartUpdatedMessage || ''
  };
  const shopifyRoot = cartShopifyRoot();
  if (!shopifyRoot) {
    forceCartPageReload(shopifyRoot);
    return;
  }

  setCartPageBusy(true);
  enqueueCartMutation(async () => {
    let removalConfirmed = false;
    let mutationStarted = false;
    try {
      const authoritativeLine = await acquireAuthoritativeCartLine(transaction, shopifyRoot);
      const previousQuantity = Number.parseInt(authoritativeLine?.item?.quantity, 10);
      if (!authoritativeLine || !Number.isInteger(previousQuantity) || previousQuantity < 1) {
        throw new Error('Cart line is unavailable');
      }

      const restorePlan = cartRestorePlanForLine(authoritativeLine.cartState, authoritativeLine.item);
      if (!restorePlan) throw new Error('Cart restore plan is unavailable');
      transaction.restorePlan = restorePlan;
      const quantityBefore = cartIdentityQuantity(authoritativeLine.cartState, transaction.identity);

      await runSignatureViewTransition(() => {
        authoritativeLine.line.hidden = true;
        authoritativeLine.line.setAttribute('aria-hidden', 'true');
      }, 'cart');
      mutationStarted = true;
      const result = await changeCartLine(String(authoritativeLine.item.key), 0, shopifyRoot);
      if (result.reloading) return;
      removalConfirmed = result.mutationAccepted;

      if (!removalConfirmed) {
        const latestCart = await fetchAuthoritativeCart(shopifyRoot);
        removalConfirmed = cartIdentityQuantity(latestCart, transaction.identity)
          <= Math.max(0, quantityBefore - previousQuantity);
      }
    } catch (error) {
      if (mutationStarted) {
        forceCartPageReload(shopifyRoot);
        return;
      }
    } finally {
      if (!cartPageReloading) {
        setCartPageBusy(false);
        if (removalConfirmed) showCartRemovalUndo(transaction, shopifyRoot);
        else showCartMutationError(transaction);
      }
    }
  });
}

document.addEventListener('click', event => {
  const removeLink = event.target.closest?.(`#${cartItemsSectionId} [data-remove]`);
  if (removeLink) {
    event.preventDefault();
    beginCartRemoval(removeLink);
    return;
  }

  const quantityButton = event.target.closest?.(
    `#${cartItemsSectionId} [data-quantity-minus], #${cartItemsSectionId} [data-quantity-plus]`
  );
  if (quantityButton) beginCartQuantityChange(quantityButton);
});

document.addEventListener('change', event => {
  const quantityInput = event.target.closest?.(`#${cartItemsSectionId} [data-quantity-input]`);
  if (quantityInput) beginCartQuantityChange(quantityInput);
});

/* ---------- server-rendered product variant selection ---------- */
const variantSectionRequests = new Map();
let variantRequestToken = 0;

function setProductVariantLoading(mainProduct, sectionId, requestState, busy){
  const requestToken = String(requestState.token);
  const groupWrappers = productAddWrappersForGroup(sectionId);
  const payments = productPaymentContainersForGroup(sectionId);

  if (busy) {
    if (!mainProduct.dataset.variantRequestToken) {
      mainProduct.dataset.variantPreviousAriaBusy = mainProduct.getAttribute('aria-busy') || '';
    }
    mainProduct.dataset.variantRequestToken = requestToken;
    mainProduct.setAttribute('aria-busy', 'true');

    groupWrappers.forEach(wrapper => {
      wrapper.dataset.productVariantPending = 'true';
      wrapper.dataset.variantRequestToken = requestToken;
    });

    payments.forEach(payment => {
      if (!payment.dataset.variantRequestToken) {
        payment.dataset.variantWasInert = payment.inert ? 'true' : 'false';
        payment.dataset.variantPreviousAriaDisabled = payment.getAttribute('aria-disabled') || '';
      }
      payment.dataset.variantRequestToken = requestToken;
      payment.inert = true;
      payment.setAttribute('aria-disabled', 'true');
    });
    return;
  }

  if (mainProduct.dataset.variantRequestToken === requestToken) {
    const previousAriaBusy = mainProduct.dataset.variantPreviousAriaBusy;
    if (previousAriaBusy) mainProduct.setAttribute('aria-busy', previousAriaBusy);
    else mainProduct.removeAttribute('aria-busy');
    delete mainProduct.dataset.variantPreviousAriaBusy;
    delete mainProduct.dataset.variantRequestToken;
  }

  groupWrappers.forEach(wrapper => {
    if (wrapper.dataset.variantRequestToken !== requestToken) return;
    delete wrapper.dataset.productVariantPending;
    delete wrapper.dataset.variantRequestToken;
  });

  payments.forEach(payment => {
    if (payment.dataset.variantRequestToken !== requestToken) return;
    payment.inert = payment.dataset.variantWasInert === 'true';
    const previousAriaDisabled = payment.dataset.variantPreviousAriaDisabled;
    if (previousAriaDisabled) payment.setAttribute('aria-disabled', previousAriaDisabled);
    else payment.removeAttribute('aria-disabled');
    delete payment.dataset.variantWasInert;
    delete payment.dataset.variantPreviousAriaDisabled;
    delete payment.dataset.variantRequestToken;
  });
}

function selectedOptionValues(picker){
  const selects = [...picker.querySelectorAll('select[data-option-position]')];
  const selections = selects.map(select => ({
    position: Number.parseInt(select.dataset.optionPosition, 10),
    id: select.selectedOptions[0]?.dataset.optionValueId || ''
  }));

  if (selections.some(selection => !selection.position || !selection.id)) return [];
  return selections.sort((first, second) => first.position - second.position);
}

function restoreRenderedSwatchSelection(select, value){
  const inputs = select.closest('.optgroup')?.querySelectorAll('[data-variant-swatch]') || [];
  const renderedInput = [...inputs].find(input => input.value === value);
  if (renderedInput) renderedInput.checked = true;
}

function fallbackOptionValueSets(selections, changedPosition){
  const changed = selections.find(selection => selection.position === changedPosition);
  if (!changed) return [];

  const fullSignature = selections.map(selection => selection.id).join(',');
  const otherSelections = selections
    .filter(selection => selection.position !== changedPosition)
    .sort((first, second) => second.position - first.position);
  const candidates = otherSelections.map(omitted =>
    selections
      .filter(selection => selection.position !== omitted.position)
      .map(selection => selection.id)
  );
  candidates.push([changed.id]);

  const signatures = new Set();
  return candidates.filter(candidate => {
    const signature = candidate.join(',');
    if (!signature || signature === fullSignature || signatures.has(signature)) return false;
    signatures.add(signature);
    return true;
  });
}

function parseProductSection(sectionHtml, sectionId){
  const documentFragment = new DOMParser().parseFromString(sectionHtml, 'text/html');
  const wrapper = documentFragment.getElementById(`shopify-section-${sectionId}`);
  const mainProduct = wrapper?.querySelector('[data-main-product]');
  return wrapper && mainProduct ? { wrapper, mainProduct } : null;
}

function hasValidVariant(sectionState, changedPosition, changedOptionValueId){
  if (!sectionState?.mainProduct.dataset.selectedVariantId) return false;
  if (sectionState.mainProduct.dataset.selectedVariantAvailable !== 'true') return false;

  const changedSelect = [...sectionState.mainProduct.querySelectorAll('select[data-option-position]')]
    .find(select => Number.parseInt(select.dataset.optionPosition, 10) === changedPosition);
  return changedSelect?.selectedOptions[0]?.dataset.optionValueId === changedOptionValueId;
}

async function requestProductSection(sectionId, optionValueIds, signal){
  const url = new URL(window.location.pathname, window.location.origin);
  url.searchParams.set('sections', sectionId);
  url.searchParams.set('option_values', optionValueIds.join(','));

  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error('Product section request failed');

  const sections = await response.json();
  const sectionHtml = sections?.[sectionId];
  if (typeof sectionHtml !== 'string' || !sectionHtml) {
    throw new Error('Product section response was empty');
  }
  return sectionHtml;
}

function updateVariantUrl(variantId){
  if (!variantId) return;

  try {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    url.searchParams.delete('option_values');
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`
    );
  } catch (error) {
    /* A URL update must never roll back an otherwise successful variant render. */
  }
}

document.addEventListener('change', async event => {
  const swatchInput = event.target.closest?.('[data-variant-swatch]');
  if (swatchInput) {
    const picker = swatchInput.closest('[data-variant-picker]');
    const position = Number.parseInt(swatchInput.dataset.optionPosition, 10);
    const linkedSelect = [...(picker?.querySelectorAll('select[data-option-position]') || [])]
      .find(select => Number.parseInt(select.dataset.optionPosition, 10) === position);
    if (!linkedSelect) return;

    const linkedOption = [...linkedSelect.options]
      .find(option => option.dataset.optionValueId === swatchInput.dataset.optionValueId);
    if (!linkedOption) return;

    linkedSelect.dataset.variantFocusId = swatchInput.id;
    linkedSelect.value = linkedOption.value;
    linkedSelect.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }

  const select = event.target.closest?.('[data-variant-picker] select[data-option-position]');
  if (!select) return;

  const picker = select.closest('[data-variant-picker]');
  const mainProduct = select.closest('[data-main-product]');
  const activeAddGroup = mainProduct?.dataset.sectionId || '';
  if (activeAddGroup && activeProductAdds.has(activeAddGroup)) {
    const renderedValue = select.querySelector('option[selected]')?.value;
    if (renderedValue != null) {
      select.value = renderedValue;
      restoreRenderedSwatchSelection(select, renderedValue);
    }
    return;
  }
  const sectionId = mainProduct?.dataset.sectionId;
  const changedPosition = Number.parseInt(select.dataset.optionPosition, 10);
  const changedOptionValueId = select.selectedOptions[0]?.dataset.optionValueId || '';
  const selections = selectedOptionValues(picker);
  const sectionWrapper = sectionId ? document.getElementById(`shopify-section-${sectionId}`) : null;
  if (!sectionId || !changedPosition || !changedOptionValueId || !selections.length || !sectionWrapper) return;

  const previousValue = select.querySelector('option[selected]')?.value;
  const focusId = select.dataset.variantFocusId || select.id;
  const quantityValue = sectionWrapper.querySelector('[data-quantity-input]')?.value;
  const previousRequest = variantSectionRequests.get(sectionId);
  previousRequest?.controller.abort();

  const requestState = {
    controller: new AbortController(),
    token: ++variantRequestToken
  };
  variantSectionRequests.set(sectionId, requestState);
  setProductVariantLoading(mainProduct, sectionId, requestState, true);

  const requestIsCurrent = () => variantSectionRequests.get(sectionId)?.token === requestState.token;

  try {
    let sectionHtml = await requestProductSection(
      sectionId,
      selections.map(selection => selection.id),
      requestState.controller.signal
    );
    if (!requestIsCurrent()) return;

    let sectionState = parseProductSection(sectionHtml, sectionId);
    if (!hasValidVariant(sectionState, changedPosition, changedOptionValueId)) {
      sectionHtml = '';
      for (const fallbackValues of fallbackOptionValueSets(selections, changedPosition)) {
        const fallbackHtml = await requestProductSection(
          sectionId,
          fallbackValues,
          requestState.controller.signal
        );
        if (!requestIsCurrent()) return;

        const fallbackState = parseProductSection(fallbackHtml, sectionId);
        if (!hasValidVariant(fallbackState, changedPosition, changedOptionValueId)) continue;
        sectionHtml = fallbackHtml;
        sectionState = fallbackState;
        break;
      }
    }

    if (!sectionHtml || !sectionState || !requestIsCurrent()) {
      if (previousValue != null) {
        select.value = previousValue;
        restoreRenderedSwatchSelection(select, previousValue);
      }
      return;
    }

    const applyVariantSection = () => {
      if (!requestIsCurrent()) return null;
      const nextWrapper = sectionState.wrapper;
      const currentBuilder = sectionWrapper.querySelector('#gs-builder-btn');
      const nextBuilder = nextWrapper.querySelector('#gs-builder-btn');
      const currentBuilderBlock = currentBuilder?.closest('.shopify-app-block');
      const nextBuilderBlock = nextBuilder?.closest('.shopify-app-block');
      // Drip binds its button once. Keep the live app instance across size changes.
      if (currentBuilderBlock && nextBuilderBlock
        && currentBuilderBlock.id === nextBuilderBlock.id
        && currentBuilder.dataset.product === nextBuilder.dataset.product) {
        nextBuilderBlock.replaceWith(currentBuilderBlock);
      }
      cleanupProductDecks(sectionWrapper);
      cleanupQuantitySelectors(sectionWrapper);
      cleanupPromiseCountdowns(sectionWrapper);
      sectionWrapper.replaceWith(nextWrapper);

      activatePrimaryProductTransitionTarget();
      const nextQuantity = nextWrapper.querySelector('[data-quantity-input]');
      if (nextQuantity && quantityValue != null) nextQuantity.value = quantityValue;
      initializeProductDecks(nextWrapper);
      initializeQuantitySelectors(nextWrapper);
      initializePromiseCountdowns(nextWrapper);
      restorePendingProductAddState(nextWrapper);
      window.Shopify?.PaymentButton?.init?.();

      const focusTarget = document.getElementById(focusId);
      const drawerIsOpen = document.getElementById(cartDrawerSectionId)?.classList.contains('is-open');
      if (!drawerIsOpen && focusTarget && nextWrapper.contains(focusTarget)) focusTarget.focus({ preventScroll: true });

      const nextMainProduct = nextWrapper.querySelector('[data-main-product]');
      updateVariantUrl(nextMainProduct?.dataset.selectedVariantId);
      return nextWrapper;
    };

    const nextWrapper = await runSignatureViewTransition(applyVariantSection, 'variant');
    if (!nextWrapper) return;
  } catch (error) {
    if (error.name !== 'AbortError' && previousValue != null) {
      select.value = previousValue;
      restoreRenderedSwatchSelection(select, previousValue);
    }
  } finally {
    if (requestIsCurrent()) {
      variantSectionRequests.delete(sectionId);
      setProductVariantLoading(mainProduct, sectionId, requestState, false);
    }
  }
});

/* ---------- local-pickup countdown on store time ---------- */
// snippets/pickup-countdown.liquid emits the upcoming pickup windows as absolute epoch
// seconds that Liquid computed in the STORE's time zone ("dayStart:cutoff,dayStart:cutoff").
// Absolute instants are what make this safe under Shopify's full page cache and prerendered
// navigations: a stale page finds no live window and falls back to the closed wording instead
// of counting down to a moment that already passed. No time-zone maths happens in the browser.
const promiseCountdownSelector = '[data-promise-pickup]';

function parsePromiseWindows(value){
  return (value || '').split(',').reduce((windows, entry) => {
    const [start, end] = entry.split(':');
    const from = Number.parseInt(start, 10);
    const until = Number.parseInt(end, 10);
    if (Number.isInteger(from) && Number.isInteger(until) && until > from) windows.push([from * 1000, until * 1000]);
    return windows;
  }, []);
}

function initializePromiseCountdown(root){
  if (root.promiseCountdownCleanup) return;
  const visual = root.querySelector('[data-promise-visual]');
  const spoken = root.querySelector('[data-promise-sr]');
  const windows = parsePromiseWindows(root.dataset.windows);
  const openTemplate = root.dataset.open || '';
  const countdownTemplate = root.dataset.countdown || '';
  const closedText = root.dataset.closed || '';
  const upcomingTemplate = root.dataset.upcoming || '';
  const staticText = root.dataset.static || '';
  if (!visual || !windows.length || !openTemplate || !countdownTemplate) return;

  let timer = 0;
  // The visible sentence is aria-hidden and mirrored by a static sentence in the
  // screen-reader span, so assistive technology never hears a self-updating value and no
  // live region is needed.
  const setCopy = (text, spokenText) => {
    visual.replaceChildren(text);
    if (spoken) spoken.textContent = spokenText ?? text;
  };
  let paintedTemplate = '';
  const paintCountdown = (remaining, template) => {
    let value = visual.querySelector('[data-promise-countdown]');
    if (!value || paintedTemplate !== template) {
      // replaceChildren takes the prefix and suffix as text nodes, so the space before the
      // number survives here exactly as the &#32; entity preserves it in server-rendered markup.
      const [prefix = '', suffix = ''] = template.split('__TIME__');
      paintedTemplate = template;
      value = document.createElement('span');
      value.className = 'pickup-countdown__value';
      value.setAttribute('data-promise-countdown', '');
      visual.replaceChildren(prefix, value, suffix);
    }
    value.textContent = countdownTemplate
      .replace('__HOURS__', String(Math.floor(remaining / 36e5)))
      .replace('__MINUTES__', String(Math.floor(remaining % 36e5 / 6e4)));
    if (spoken) spoken.textContent = staticText || closedText;
  };
  const render = () => {
    window.clearTimeout(timer);
    const now = Date.now();
    // The first window that has not ended yet: inside it the number is today's deadline,
    // before it starts the same number counts to the next cut-off instead of going quiet.
    const next = windows.find(([, until]) => now < until);
    if (!next) {
      // Out of rendered windows: the closed wording makes no dated promise, so a stale page
      // stays true until the next load.
      setCopy(closedText);
      return;
    }
    const live = now >= next[0] ? next : null;
    const template = live ? openTemplate : (upcomingTemplate || openTemplate);
    // WCAG 2.2.2 stop mechanism: with reduced motion the sentence is the static cutoff, never
    // a ticking number. One timeout still flips it to the closed wording at the cutoff,
    // because a frozen "pickup today" would become false rather than merely still.
    if (userPrefersReducedMotion()) {
      setCopy(staticText || closedText);
      timer = window.setTimeout(render, Math.min(next[1] - now + 250, 216e5));
      return;
    }
    const remaining = next[1] - now;
    paintCountdown(remaining, template);
    // Wake exactly when the displayed minute changes, never more often.
    timer = window.setTimeout(render, remaining % 6e4 + 250);
  };
  const visibilityHandler = () => {
    if (!document.hidden) render();
  };

  document.addEventListener('visibilitychange', visibilityHandler);
  root.promiseCountdownCleanup = () => {
    window.clearTimeout(timer);
    document.removeEventListener('visibilitychange', visibilityHandler);
    delete root.promiseCountdownCleanup;
  };
  render();
}

function initializePromiseCountdowns(scope){
  if (scope.matches?.(promiseCountdownSelector)) initializePromiseCountdown(scope);
  scope.querySelectorAll?.(promiseCountdownSelector).forEach(initializePromiseCountdown);
}
function cleanupPromiseCountdowns(scope){
  if (scope.matches?.(promiseCountdownSelector)) scope.promiseCountdownCleanup?.();
  scope.querySelectorAll?.(promiseCountdownSelector).forEach(root => root.promiseCountdownCleanup?.());
}
initializePromiseCountdowns(document);
document.addEventListener('shopify:section:load', event => initializePromiseCountdowns(event.target));
document.addEventListener('shopify:section:unload', event => cleanupPromiseCountdowns(event.target));

})();
